import { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { 
  Download, Upload, Database, Archive, RefreshCw, Trash2, 
  CheckCircle, AlertTriangle, Clock, Shield, Activity, FileText,
  RotateCcw, History, HeartPulse, Search, Wrench, AlertCircle,
  Hash, ChevronDown, ChevronRight, Bell, Mail, Send, Play, Calendar,
  Building2, Users, UserX, LogOut, Crown, UserCog, Eye, Plus, Edit, Palette,
  ShieldAlert, Ban, Filter, UserPlus, Bug, MessageSquare
} from 'lucide-react';
import { toast } from 'sonner';
import { Textarea } from '@/components/ui/textarea';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminTools() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [systemStats, setSystemStats] = useState(null);
  const [archivedActions, setArchivedActions] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [backups, setBackups] = useState([]);
  const [referenceCheck, setReferenceCheck] = useState(null);
  const [dbHealth, setDbHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [cleanupResults, setCleanupResults] = useState(null);
  const [cleanupDialogs, setCleanupDialogs] = useState({
    owners: false,
    categories: false,
    meetings: false,
    notes: false,
    agendaItems: false,
    orgReports: false
  });
  const [renumberResults, setRenumberResults] = useState(null);
  const [renumberLoading, setRenumberLoading] = useState(false);
  const [showRenumberDialog, setShowRenumberDialog] = useState(false);
  const [auditArchiveResults, setAuditArchiveResults] = useState(null);
  const [auditDaysToKeep, setAuditDaysToKeep] = useState('90');
  const [showAuditArchiveDialog, setShowAuditArchiveDialog] = useState(false);
  const [auditLogOpen, setAuditLogOpen] = useState(true);
  const [loading, setLoading] = useState(true);
  const [showRestoreDialog, setShowRestoreDialog] = useState(false);
  const [restoreFile, setRestoreFile] = useState(null);
  const [actionToDelete, setActionToDelete] = useState(null);
  const [emailReminderResults, setEmailReminderResults] = useState(null);
  const [emailLoading, setEmailLoading] = useState(false);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  const [allActions, setAllActions] = useState([]);
  const [showEmailReminderDialog, setShowEmailReminderDialog] = useState(false);
  const [orphanResults, setOrphanResults] = useState(null);
  const [dateNormResults, setDateNormResults] = useState(null);
  
  // Data migration state
  const [migrationLoading, setMigrationLoading] = useState(false);
  const [migrationResults, setMigrationResults] = useState(null);
  const [actionsMissingClosedBy, setActionsMissingClosedBy] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [manualClosedByLoading, setManualClosedByLoading] = useState(false);
  
  // Scheduler state
  const [schedulerSettings, setSchedulerSettings] = useState(null);
  const [schedulerHistory, setSchedulerHistory] = useState([]);
  const [schedulerLoading, setSchedulerLoading] = useState(false);

  // Digest state
  const [digestSettings, setDigestSettings] = useState(null);
  const [digestHistory, setDigestHistory] = useState([]);
  const [digestLoading, setDigestLoading] = useState(false);
  const [digestPreview, setDigestPreview] = useState(null);
  const [showDigestPreview, setShowDigestPreview] = useState(false);

  // System settings (feature flags)
  const [systemSettings, setSystemSettings] = useState({ meetings_enabled: false });
  const [settingsLoading, setSettingsLoading] = useState(false);

  // Groups management state
  const [groups, setGroups] = useState([]);
  const [groupMembers, setGroupMembers] = useState({});
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [showEditGroup, setShowEditGroup] = useState(false);
  const [showAddMember, setShowAddMember] = useState(false);
  const [addMemberMode, setAddMemberMode] = useState('existing'); // 'existing' or 'create'
  const [newUserForGroup, setNewUserForGroup] = useState({ name: '', email: '', username: '', phone: '', group_role: 'member', send_invitation: true });
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [newGroup, setNewGroup] = useState({ name: '', short_name: '', prefix: '', description: '', color: '#3B82F6' });
  const [allUsers, setAllUsers] = useState([]);

  // Sessions state
  const [sessions, setSessions] = useState({ sessions: [], stats: {} });
  const [sessionsLoading, setSessionsLoading] = useState(false);

  // Permission Audit state
  const [permissionAudit, setPermissionAudit] = useState({ logs: [], total: 0, stats: {} });
  const [permissionAuditLoading, setPermissionAuditLoading] = useState(false);
  const [permissionAuditFilter, setPermissionAuditFilter] = useState({ permission: '', resource_type: '' });

  // Support Tickets state
  const [supportTickets, setSupportTickets] = useState([]);
  const [supportTicketsLoading, setSupportTicketsLoading] = useState(false);
  const [ticketStats, setTicketStats] = useState({ total: 0, open: 0, in_progress: 0, resolved: 0, closed: 0 });
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketAdminNotes, setTicketAdminNotes] = useState('');

  useEffect(() => {
    if (user?.role !== 'super_admin') {
      navigate('/dashboard');
      return;
    }
    fetchData();
    fetchSchedulerData();
    fetchDigestData();
    fetchSystemSettings();
    fetchSupportTickets();
    fetchGroupsData();
    fetchSessionsData();
    fetchPermissionAuditData();
    fetchActionsMissingClosedBy();
  }, [user, navigate]);

  const fetchSystemSettings = async () => {
    try {
      const res = await axios.get(`${API}/system/settings`);
      setSystemSettings(res.data);
    } catch (error) {
      console.error('Failed to fetch system settings');
    }
  };

  const toggleMeetingsEnabled = async () => {
    setSettingsLoading(true);
    try {
      const newValue = !systemSettings.meetings_enabled;
      await axios.put(`${API}/system/settings`, { meetings_enabled: newValue });
      setSystemSettings({ ...systemSettings, meetings_enabled: newValue });
      toast.success(`Meetings module ${newValue ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error('Failed to update settings');
    } finally {
      setSettingsLoading(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, archivedRes, auditRes, backupsRes, refCheckRes, actionsRes] = await Promise.all([
        axios.get(`${API}/admin/system-stats`),
        axios.get(`${API}/actions/archived`),
        axios.get(`${API}/admin/audit-log?limit=50`),
        axios.get(`${API}/admin/backups`),
        axios.get(`${API}/admin/reference-check`),
        axios.get(`${API}/actions`)
      ]);
      setSystemStats(statsRes.data);
      setArchivedActions(archivedRes.data);
      setAuditLog(auditRes.data);
      setBackups(backupsRes.data);
      setReferenceCheck(refCheckRes.data);
      setAllActions(actionsRes.data.filter(a => !a.is_archived));
    } catch (error) {
      toast.error('Failed to load admin data: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const fetchSchedulerData = async () => {
    try {
      const [settingsRes, historyRes] = await Promise.all([
        axios.get(`${API}/scheduler/settings`),
        axios.get(`${API}/scheduler/history?limit=10`)
      ]);
      setSchedulerSettings(settingsRes.data);
      setSchedulerHistory(historyRes.data);
    } catch (error) {
      console.error('Failed to load scheduler data:', error);
    }
  };

  const fetchDigestData = async () => {
    try {
      const [settingsRes, historyRes] = await Promise.all([
        axios.get(`${API}/digest/settings`),
        axios.get(`${API}/digest/history?limit=10`)
      ]);
      setDigestSettings(settingsRes.data);
      setDigestHistory(historyRes.data);
    } catch (error) {
      console.error('Failed to load digest data:', error);
    }
  };

  // Groups management functions
  const fetchGroupsData = async () => {
    setGroupsLoading(true);
    try {
      const [groupsRes, usersRes] = await Promise.all([
        axios.get(`${API}/groups`),
        axios.get(`${API}/users`)
      ]);
      setGroups(groupsRes.data);
      setAllUsers(usersRes.data);
      
      // Pre-fetch member counts for all groups
      const memberCounts = {};
      await Promise.all(
        groupsRes.data.map(async (group) => {
          try {
            const membersRes = await axios.get(`${API}/groups/${group.id}/members`);
            memberCounts[group.id] = membersRes.data;
          } catch (err) {
            memberCounts[group.id] = [];
          }
        })
      );
      setGroupMembers(memberCounts);
    } catch (error) {
      console.error('Failed to load groups data:', error);
    } finally {
      setGroupsLoading(false);
    }
  };

  const fetchGroupMembers = async (groupId) => {
    try {
      const res = await axios.get(`${API}/groups/${groupId}/members`);
      setGroupMembers(prev => ({ ...prev, [groupId]: res.data }));
    } catch (error) {
      console.error('Failed to load group members:', error);
    }
  };

  const handleCreateGroup = async () => {
    try {
      await axios.post(`${API}/groups`, newGroup);
      toast.success(`Group "${newGroup.name}" created successfully`);
      setShowCreateGroup(false);
      setNewGroup({ name: '', short_name: '', prefix: '', description: '', color: '#3B82F6' });
      fetchGroupsData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create group');
    }
  };

  const handleUpdateGroup = async () => {
    try {
      await axios.put(`${API}/groups/${selectedGroup.id}`, {
        name: selectedGroup.name,
        short_name: selectedGroup.short_name,
        description: selectedGroup.description,
        color: selectedGroup.settings?.color
      });
      toast.success('Group updated successfully');
      setShowEditGroup(false);
      fetchGroupsData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update group');
    }
  };

  const handleArchiveGroup = async (groupId, groupName) => {
    if (!confirm(`Are you sure you want to archive "${groupName}"? This will hide the group but preserve its data.`)) return;
    try {
      await axios.delete(`${API}/groups/${groupId}`);
      toast.success(`Group "${groupName}" archived`);
      fetchGroupsData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to archive group');
    }
  };

  const handleAddMember = async (groupId, userId, role) => {
    try {
      await axios.post(`${API}/groups/${groupId}/members`, { user_id: userId, role });
      toast.success('Member added to group');
      setShowAddMember(false);
      fetchGroupMembers(groupId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add member');
    }
  };

  const handleCreateUserForGroup = async (groupId) => {
    try {
      const response = await axios.post(`${API}/groups/${groupId}/members/create`, newUserForGroup);
      if (response.data.invitation_sent) {
        toast.success(`User created and invitation sent to ${newUserForGroup.email}`);
      } else if (response.data.email_error) {
        toast.warning(`User created but invitation email failed: ${response.data.email_error}`);
      } else {
        toast.success('User created and added to group');
      }
      setShowAddMember(false);
      setAddMemberMode('existing');
      setNewUserForGroup({ name: '', email: '', username: '', phone: '', group_role: 'member', send_invitation: true });
      fetchGroupMembers(groupId);
      fetchGroupsData(); // Refresh user list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleUpdateMemberRole = async (groupId, userId, newRole) => {
    try {
      await axios.put(`${API}/groups/${groupId}/members/${userId}`, { role: newRole });
      toast.success('Member role updated');
      fetchGroupMembers(groupId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update role');
    }
  };

  const handleRemoveMember = async (groupId, userId, userName) => {
    if (!confirm(`Are you sure you want to remove ${userName} from this group?`)) return;
    try {
      await axios.delete(`${API}/groups/${groupId}/members/${userId}`);
      toast.success(`${userName} removed from group`);
      fetchGroupMembers(groupId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove member');
    }
  };

  // Sessions management functions
  const fetchSessionsData = async () => {
    setSessionsLoading(true);
    try {
      const res = await axios.get(`${API}/admin/sessions`);
      setSessions(res.data);
    } catch (error) {
      console.error('Failed to load sessions data:', error);
    } finally {
      setSessionsLoading(false);
    }
  };

  const handleTerminateSession = async (sessionToken, userName) => {
    if (!confirm(`Are you sure you want to terminate session for ${userName}? They will need to log in again.`)) return;
    try {
      await axios.delete(`${API}/admin/sessions/${sessionToken}`);
      toast.success(`Session terminated for ${userName}`);
      fetchSessionsData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to terminate session');
    }
  };

  const handleTerminateUserSessions = async (userId, userName) => {
    if (!confirm(`Are you sure you want to terminate ALL sessions for ${userName}?`)) return;
    try {
      const res = await axios.delete(`${API}/admin/sessions/user/${userId}`);
      toast.success(res.data.message);
      fetchSessionsData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to terminate sessions');
    }
  };

  const handleCleanupExpiredSessions = async () => {
    try {
      const res = await axios.delete(`${API}/admin/sessions`);
      toast.success(res.data.message);
      fetchSessionsData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cleanup sessions');
    }
  };

  const handleCleanupOldSessions = async (days) => {
    if (!confirm(`Are you sure you want to delete all sessions older than ${days} day(s)? Users will need to log in again.`)) return;
    try {
      const res = await axios.delete(`${API}/admin/sessions?older_than_days=${days}`);
      toast.success(res.data.message);
      fetchSessionsData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cleanup sessions');
    }
  };

  // Permission Audit functions
  const fetchPermissionAuditData = async () => {
    setPermissionAuditLoading(true);
    try {
      const [logsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/permission-audit`, { params: { limit: 100 } }),
        axios.get(`${API}/admin/permission-audit/stats`)
      ]);
      setPermissionAudit({ 
        logs: logsRes.data.logs, 
        total: logsRes.data.total,
        stats: statsRes.data 
      });
    } catch (error) {
      console.error('Failed to load permission audit data:', error);
    } finally {
      setPermissionAuditLoading(false);
    }
  };

  const handleCleanupPermissionAudit = async (days) => {
    if (!confirm(`Delete permission audit logs older than ${days} days?`)) return;
    try {
      const res = await axios.delete(`${API}/admin/permission-audit/cleanup?days_to_keep=${days}`);
      toast.success(`Deleted ${res.data.deleted_count} old permission audit logs`);
      fetchPermissionAuditData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cleanup logs');
    }
  };

  // Support Tickets functions
  const fetchSupportTickets = async () => {
    setSupportTicketsLoading(true);
    try {
      const [ticketsRes, statsRes] = await Promise.all([
        axios.get(`${API}/support/tickets`),
        axios.get(`${API}/support/tickets/stats/summary`)
      ]);
      setSupportTickets(ticketsRes.data);
      setTicketStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch support tickets:', error);
    } finally {
      setSupportTicketsLoading(false);
    }
  };

  const handleUpdateTicketStatus = async (ticketId, newStatus) => {
    try {
      // When resolving/closing, include admin notes in the same request
      const updateData = { status: newStatus };
      if ((newStatus === 'resolved' || newStatus === 'closed') && ticketAdminNotes) {
        updateData.admin_notes = ticketAdminNotes;
      }
      await axios.patch(`${API}/support/tickets/${ticketId}`, updateData);
      toast.success(`Ticket ${newStatus === 'resolved' ? 'marked as resolved' : 'updated'}`);
      fetchSupportTickets();
      setSelectedTicket(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update ticket');
    }
  };

  const handleSaveTicketNotes = async (ticketId) => {
    try {
      await axios.patch(`${API}/support/tickets/${ticketId}`, { admin_notes: ticketAdminNotes });
      toast.success('Notes saved');
      // Update the selected ticket with new notes immediately
      setSelectedTicket(prev => prev ? {...prev, admin_notes: ticketAdminNotes} : null);
      fetchSupportTickets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save notes');
    }
  };

  const handleDeleteTicket = async (ticketId) => {
    if (!confirm('Delete this support ticket? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/support/tickets/${ticketId}`);
      toast.success('Ticket deleted');
      fetchSupportTickets();
      setSelectedTicket(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete ticket');
    }
  };

  const updateDigestSettings = async (updates) => {
    setDigestLoading(true);
    try {
      const response = await axios.put(`${API}/digest/settings`, updates);
      // API returns {message: "...", settings: {...}}
      setDigestSettings(response.data.settings);
      toast.success(updates.enabled !== undefined 
        ? (updates.enabled ? 'Weekly digest enabled' : 'Weekly digest disabled')
        : 'Digest settings updated');
    } catch (error) {
      toast.error('Failed to update digest: ' + (error.response?.data?.detail || error.message));
    } finally {
      setDigestLoading(false);
    }
  };

  const previewDigest = async () => {
    setDigestLoading(true);
    try {
      const response = await axios.post(`${API}/digest/preview`);
      setDigestPreview(response.data);
      setShowDigestPreview(true);
    } catch (error) {
      toast.error('Failed to generate preview: ' + (error.response?.data?.detail || error.message));
    } finally {
      setDigestLoading(false);
    }
  };

  const sendDigestNow = async () => {
    setDigestLoading(true);
    try {
      const response = await axios.post(`${API}/digest/send-now`);
      toast.success(`Digest sent to ${response.data.emails_sent} recipient(s)`);
      setShowDigestPreview(false);
      // Refresh history after a delay
      setTimeout(() => {
        fetchDigestData();
      }, 2000);
    } catch (error) {
      toast.error('Failed to send digest: ' + (error.response?.data?.detail || error.message));
    } finally {
      setDigestLoading(false);
    }
  };

  const clearDigestHistory = async () => {
    setDigestLoading(true);
    try {
      const response = await axios.delete(`${API}/digest/history`);
      toast.success(response.data.message);
      setDigestHistory([]);
    } catch (error) {
      toast.error('Failed to clear history: ' + (error.response?.data?.detail || error.message));
    } finally {
      setDigestLoading(false);
    }
  };

  const updateSchedulerSettings = async (updates) => {
    setSchedulerLoading(true);
    try {
      const response = await axios.put(`${API}/scheduler/settings`, updates);
      setSchedulerSettings(response.data);
      toast.success(updates.enabled !== undefined 
        ? (updates.enabled ? 'Automated reminders enabled' : 'Automated reminders disabled')
        : 'Scheduler settings updated');
    } catch (error) {
      toast.error('Failed to update scheduler: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSchedulerLoading(false);
    }
  };

  const runSchedulerNow = async () => {
    setSchedulerLoading(true);
    try {
      await axios.post(`${API}/scheduler/run-now`);
      toast.success('Sending reminders... Check history for results.');
      // Refresh history after a delay
      setTimeout(() => {
        fetchSchedulerData();
      }, 3000);
    } catch (error) {
      toast.error('Failed to run scheduler: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSchedulerLoading(false);
    }
  };

  const fetchAllActions = async () => {
    try {
      const response = await axios.get(`${API}/actions`);
      setAllActions(response.data.filter(a => !a.is_archived));
    } catch (error) {
      console.error('Failed to fetch actions:', error);
    }
  };

  const handleBulkDeleteAll = async () => {
    setBulkDeleteLoading(true);
    try {
      // Delete all non-archived actions
      const response = await axios.delete(`${API}/admin/actions/bulk-delete`);
      toast.success(`Deleted ${response.data.deleted_count} actions`);
      setShowBulkDeleteDialog(false);
      fetchAllActions();
      runDatabaseHealthCheck();
    } catch (error) {
      toast.error('Failed to delete actions: ' + (error.response?.data?.detail || error.message));
    } finally {
      setBulkDeleteLoading(false);
    }
  };

  const handleCreateBackup = async () => {
    try {
      toast.info('Creating backup...');
      const response = await axios.post(`${API}/admin/backup`, null, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = `HLRA_Backup_${new Date().toISOString().split('T')[0]}.json`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Backup created and downloaded successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to create backup: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleRestoreBackup = async () => {
    if (!restoreFile) {
      toast.error('Please select a backup file');
      return;
    }

    try {
      const fileContent = await restoreFile.text();
      
      // Validate JSON before sending
      let backupData;
      try {
        backupData = JSON.parse(fileContent);
      } catch (e) {
        toast.error('Invalid backup file: Not valid JSON');
        return;
      }
      
      if (!backupData.metadata || !backupData.data) {
        toast.error('Invalid backup file: Missing metadata or data');
        return;
      }
      
      toast.info('Restoring database...');
      
      const response = await axios.post(`${API}/admin/restore`, backupData, {
        headers: { 'Content-Type': 'application/json' }
      });
      
      toast.success(`Database restored successfully from ${response.data.backup_name}`);
      setShowRestoreDialog(false);
      setRestoreFile(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to restore backup: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleRestoreAction = async (actionId) => {
    try {
      await axios.post(`${API}/actions/${actionId}/restore`);
      toast.success('Action restored successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to restore action: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handlePermanentDelete = async () => {
    if (!actionToDelete) return;
    
    try {
      await axios.delete(`${API}/actions/${actionToDelete.id}/permanent`);
      toast.success(`Action ${actionToDelete.reference} permanently deleted`);
      setActionToDelete(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete action: ' + (error.response?.data?.detail || error.message));
    }
  };

  const runDatabaseHealthCheck = async () => {
    setHealthLoading(true);
    setDbHealth(null);
    try {
      const response = await axios.get(`${API}/admin/database-health`);
      setDbHealth(response.data);
      if (response.data.health_status === 'HEALTHY') {
        toast.success('Database is healthy - no issues found');
      } else if (response.data.health_status === 'WARNINGS') {
        toast.info(`Database check complete - ${response.data.total_warnings} warning(s) found`);
      } else {
        toast.warning(`Database check complete - ${response.data.total_issues} issue(s) found`);
      }
    } catch (error) {
      toast.error('Failed to run health check: ' + (error.response?.data?.detail || error.message));
    } finally {
      setHealthLoading(false);
    }
  };

  const runCleanup = async (cleanupType, dryRun = true, dialogKey = null) => {
    setCleanupLoading(true);
    setCleanupResults(null);
    try {
      const response = await axios.post(`${API}/admin/cleanup-orphans`, {
        cleanup_type: cleanupType,
        dry_run: dryRun
      });
      setCleanupResults(response.data);
      if (dryRun) {
        toast.info(`Preview: ${response.data.count} record(s) would be affected`);
      } else {
        toast.success(`Cleanup complete: ${response.data.count} record(s) processed`);
        // Close the dialog
        if (dialogKey) {
          setCleanupDialogs(prev => ({ ...prev, [dialogKey]: false }));
        }
        // Refresh health check
        runDatabaseHealthCheck();
      }
    } catch (error) {
      toast.error('Cleanup failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setCleanupLoading(false);
    }
  };

  const runRenumber = async (dryRun = true) => {
    setRenumberLoading(true);
    setRenumberResults(null);
    try {
      const response = await axios.post(`${API}/admin/renumber-actions`, { dry_run: dryRun });
      setRenumberResults(response.data);
      if (dryRun) {
        toast.info(`Preview: ${response.data.changes_needed} action(s) would be renumbered`);
      } else {
        toast.success(`Renumbered ${response.data.changes_needed} action(s) successfully`);
        setShowRenumberDialog(false);
        fetchData();
      }
    } catch (error) {
      toast.error('Renumber failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setRenumberLoading(false);
    }
  };

  const runClosedByMigration = async () => {
    setMigrationLoading(true);
    setMigrationResults(null);
    try {
      const response = await axios.post(`${API}/admin/migrate-closed-by`);
      setMigrationResults(response.data);
      if (response.data.migrated > 0) {
        toast.success(`Migration complete! ${response.data.migrated} action(s) updated with "Closed By" information`);
      } else if (response.data.total_closed_without_closer === 0) {
        toast.info('No actions need migration - all closed actions already have "Closed By" recorded');
      } else {
        toast.warning(`Migration complete but ${response.data.not_found} action(s) could not be updated (no audit trail found)`);
      }
      // Refresh the list of actions missing closed_by
      fetchActionsMissingClosedBy();
    } catch (error) {
      toast.error('Migration failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setMigrationLoading(false);
    }
  };

  const fetchActionsMissingClosedBy = async () => {
    try {
      const response = await axios.get(`${API}/admin/actions-missing-closed-by`);
      setActionsMissingClosedBy(response.data.actions || []);
      setAvailableUsers(response.data.users || []);
    } catch (error) {
      console.error('Failed to fetch actions missing closed_by:', error);
    }
  };

  const handleManualClosedBy = async (actionId, userId) => {
    setManualClosedByLoading(true);
    try {
      const response = await axios.put(`${API}/admin/actions/${actionId}/closed-by`, { user_id: userId });
      toast.success(response.data.message);
      // Refresh the list
      fetchActionsMissingClosedBy();
    } catch (error) {
      toast.error('Failed to set Closed By: ' + (error.response?.data?.detail || error.message));
    } finally {
      setManualClosedByLoading(false);
    }
  };

  const exportAuditLog = async () => {
    try {
      toast.info('Exporting audit log...');
      const response = await axios.get(`${API}/admin/audit-log/export`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `HLRA_AuditLog_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Audit log exported successfully');
    } catch (error) {
      toast.error('Export failed: ' + (error.response?.data?.detail || error.message));
    }
  };

  const archiveAuditLogs = async (dryRun = true) => {
    try {
      const response = await axios.post(`${API}/admin/audit-log/archive`, {
        days_to_keep: parseInt(auditDaysToKeep),
        dry_run: dryRun
      });
      setAuditArchiveResults(response.data);
      if (dryRun) {
        toast.info(`Preview: ${response.data.logs_to_archive} log(s) older than ${auditDaysToKeep} days`);
      } else {
        toast.success(`Archived ${response.data.logs_deleted} audit log(s)`);
        setShowAuditArchiveDialog(false);
        fetchData();
      }
    } catch (error) {
      toast.error('Archive failed: ' + (error.response?.data?.detail || error.message));
    }
  };

  const sendDueReminders = async (dryRun = true) => {
    setEmailLoading(true);
    setEmailReminderResults(null);
    try {
      const response = await axios.post(`${API}/notifications/send-due-reminders`, { dry_run: dryRun });
      setEmailReminderResults(response.data);
      if (dryRun) {
        toast.info(`Preview: ${response.data.total_users_with_due_actions} user(s) would receive emails`);
      } else {
        const sent = response.data.emails.filter(e => e.status === 'sent').length;
        toast.success(`Sent ${sent} email reminder(s)`);
        setShowEmailReminderDialog(false);
      }
    } catch (error) {
      toast.error('Failed to send reminders: ' + (error.response?.data?.detail || error.message));
    } finally {
      setEmailLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getChangeTypeIcon = (type) => {
    switch(type) {
      case 'created': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'updated': return <RefreshCw className="w-4 h-4 text-blue-500" />;
      case 'archived': return <Archive className="w-4 h-4 text-orange-500" />;
      case 'restored': return <RotateCcw className="w-4 h-4 text-purple-500" />;
      case 'deleted': return <Trash2 className="w-4 h-4 text-red-500" />;
      default: return <Activity className="w-4 h-4 text-gray-500" />;
    }
  };

  if (user?.role !== 'super_admin') {
    return null;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in" data-testid="admin-tools-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Admin Tools</h1>
          <p className="text-gray-600">System administration and database management</p>
        </div>
        <div className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-blue-600" />
          <span className="text-sm font-medium text-blue-600">Super Admin Access</span>
        </div>
      </div>

      {/* System Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-blue-600">{systemStats?.actions?.active || 0}</div>
            <p className="text-sm text-gray-600">Active Actions</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-orange-600">{systemStats?.actions?.archived || 0}</div>
            <p className="text-sm text-gray-600">Archived Actions</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-green-600">{systemStats?.users?.active || 0}</div>
            <p className="text-sm text-gray-600">Active Users</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-purple-600">{systemStats?.backups?.total || 0}</div>
            <p className="text-sm text-gray-600">Backups Created</p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="support" className="w-full">
        <TabsList className="grid w-full grid-cols-11">
          <TabsTrigger value="support" className="relative">
            <Bug className="w-4 h-4 mr-2" />
            Support
            {ticketStats.open > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {ticketStats.open}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="settings">
            <Shield className="w-4 h-4 mr-2" />
            Settings
          </TabsTrigger>
          <TabsTrigger value="groups">
            <Building2 className="w-4 h-4 mr-2" />
            Groups
          </TabsTrigger>
          <TabsTrigger value="sessions">
            <Users className="w-4 h-4 mr-2" />
            Sessions
          </TabsTrigger>
          <TabsTrigger value="permissions">
            <ShieldAlert className="w-4 h-4 mr-2" />
            Permissions
          </TabsTrigger>
          <TabsTrigger value="backup">
            <Database className="w-4 h-4 mr-2" />
            Backup
          </TabsTrigger>
          <TabsTrigger value="health">
            <HeartPulse className="w-4 h-4 mr-2" />
            Health
          </TabsTrigger>
          <TabsTrigger value="notifications">
            <Bell className="w-4 h-4 mr-2" />
            Email
          </TabsTrigger>
          <TabsTrigger value="archived">
            <Archive className="w-4 h-4 mr-2" />
            Archived
          </TabsTrigger>
          <TabsTrigger value="integrity">
            <CheckCircle className="w-4 h-4 mr-2" />
            Integrity
          </TabsTrigger>
          <TabsTrigger value="audit">
            <History className="w-4 h-4 mr-2" />
            Audit
          </TabsTrigger>
        </TabsList>

        {/* Support Tickets Tab */}
        <TabsContent value="support" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Bug className="w-5 h-5" />
                    Support Tickets
                  </CardTitle>
                  <CardDescription>User-reported issues and feedback</CardDescription>
                </div>
                <div className="flex gap-2">
                  <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
                    {ticketStats.open} Open
                  </Badge>
                  <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                    {ticketStats.in_progress} In Progress
                  </Badge>
                  <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                    {ticketStats.resolved + ticketStats.closed} Resolved
                  </Badge>
                  <Button size="sm" variant="outline" onClick={fetchSupportTickets}>
                    <RefreshCw className={`w-4 h-4 mr-2 ${supportTicketsLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {supportTicketsLoading ? (
                <div className="flex justify-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
                </div>
              ) : supportTickets.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Bug className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No support tickets yet</p>
                  <p className="text-sm">Users can submit issues using the &quot;Report Issue&quot; button in the sidebar</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {supportTickets.map(ticket => (
                    <div
                      key={ticket.id}
                      className={`p-4 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors ${
                        selectedTicket?.id === ticket.id ? 'ring-2 ring-blue-500 bg-blue-50' : ''
                      } ${ticket.status === 'open' ? 'border-l-4 border-l-yellow-500' : 
                          ticket.status === 'in_progress' ? 'border-l-4 border-l-blue-500' :
                          'border-l-4 border-l-green-500'}`}
                      onClick={() => {
                        setSelectedTicket(ticket);
                        setTicketAdminNotes(ticket.admin_notes || '');
                      }}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-medium text-gray-900">{ticket.title}</h3>
                            <Badge className={
                              ticket.priority === 'high' ? 'bg-red-100 text-red-700' :
                              ticket.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-green-100 text-green-700'
                            }>
                              {ticket.priority}
                            </Badge>
                            <Badge variant="outline">
                              {ticket.category === 'bug' ? '🐛' : 
                               ticket.category === 'feature' ? '💡' :
                               ticket.category === 'question' ? '❓' : '📝'} {ticket.category}
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-600 line-clamp-2">{ticket.description}</p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                            <span>From: {ticket.submitted_by_name}</span>
                            {ticket.group_name && <span>Group: {ticket.group_name}</span>}
                            <span>{new Date(ticket.created_at).toLocaleDateString()}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                          <Select
                            value={ticket.status}
                            onValueChange={(value) => handleUpdateTicketStatus(ticket.id, value)}
                          >
                            <SelectTrigger className="w-32 h-8">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="open">🟡 Open</SelectItem>
                              <SelectItem value="in_progress">🔵 In Progress</SelectItem>
                              <SelectItem value="resolved">🟢 Resolved</SelectItem>
                              <SelectItem value="closed">⚫ Closed</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Ticket Detail Dialog */}
          <Dialog open={!!selectedTicket} onOpenChange={(open) => !open && setSelectedTicket(null)}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Bug className="w-5 h-5" />
                  {selectedTicket?.title}
                </DialogTitle>
                <DialogDescription>
                  Ticket submitted on {selectedTicket && new Date(selectedTicket.created_at).toLocaleString()}
                </DialogDescription>
              </DialogHeader>
              
              {selectedTicket && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">Submitted by</p>
                      <p className="font-medium">{selectedTicket.submitted_by_name}</p>
                      <p className="text-gray-500">{selectedTicket.submitted_by_email}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Details</p>
                      <p>Group: {selectedTicket.group_name || 'N/A'}</p>
                      <p>Status: <Badge className={
                        selectedTicket.status === 'open' ? 'bg-yellow-100 text-yellow-700' :
                        selectedTicket.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                        'bg-green-100 text-green-700'
                      }>{selectedTicket.status}</Badge></p>
                      {selectedTicket.resolved_at && (
                        <p>Resolved: {new Date(selectedTicket.resolved_at).toLocaleString()}</p>
                      )}
                    </div>
                  </div>
                  
                  <div>
                    <p className="text-gray-500 text-sm mb-1">Description</p>
                    <p className="bg-gray-50 p-3 rounded border">{selectedTicket.description}</p>
                  </div>
                  
                  {selectedTicket.page_url && (
                    <div>
                      <p className="text-gray-500 text-sm mb-1">Page URL</p>
                      <p className="text-xs font-mono bg-gray-50 p-2 rounded border break-all">{selectedTicket.page_url}</p>
                    </div>
                  )}
                  
                  <div>
                    <p className="text-gray-500 text-sm mb-1">Admin Notes</p>
                    <Textarea
                      value={ticketAdminNotes}
                      onChange={(e) => setTicketAdminNotes(e.target.value)}
                      placeholder="Add internal notes about this ticket..."
                      rows={3}
                    />
                    <Button 
                      size="sm" 
                      className="mt-2 bg-blue-600 hover:bg-blue-700 text-white"
                      onClick={() => handleSaveTicketNotes(selectedTicket.id)}
                    >
                      Save Notes
                    </Button>
                  </div>
                  
                  <div className="flex justify-between items-center pt-4 border-t">
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDeleteTicket(selectedTicket.id)}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete Ticket
                    </Button>
                    <div className="flex gap-2">
                      {selectedTicket.status !== 'resolved' && selectedTicket.status !== 'closed' && (
                        <Button
                          className="bg-green-600 hover:bg-green-700"
                          size="sm"
                          onClick={() => handleUpdateTicketStatus(selectedTicket.id, 'resolved')}
                        >
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Mark as Resolved
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Settings Tab - Feature Flags */}
        <TabsContent value="settings" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-indigo-600" />
                Feature Flags
              </CardTitle>
              <CardDescription>
                Enable or disable features across the platform. Changes take effect immediately for all users.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Meetings Module Toggle */}
              <div className="flex items-center justify-between p-4 border rounded-lg bg-gray-50">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-blue-600" />
                    <h3 className="font-semibold text-gray-900">Meetings Module</h3>
                    {systemSettings.meetings_enabled ? (
                      <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                        Enabled
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">
                        Development Only
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600">
                    {systemSettings.meetings_enabled 
                      ? 'Meetings is visible to all users in the navigation menu.'
                      : 'Meetings is only visible to Super Admin for development/testing.'}
                  </p>
                  <p className="text-xs text-gray-500 mt-2">
                    When disabled, only Super Admin can access the Meetings section.
                    Enable once the module is ready for general use.
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <Switch
                    checked={systemSettings.meetings_enabled}
                    onCheckedChange={toggleMeetingsEnabled}
                    disabled={settingsLoading}
                    className="data-[state=checked]:bg-green-600"
                  />
                </div>
              </div>

              {/* Info about multi-tenancy */}
              <div className="p-4 border border-blue-200 rounded-lg bg-blue-50">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-blue-900">Multi-Tenancy Note</h4>
                    <p className="text-sm text-blue-700 mt-1">
                      When the Groups feature is implemented, each group will have its own setting 
                      to enable/disable the Meetings module. This global setting will serve as the 
                      default for new groups.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Groups Management Tab */}
        <TabsContent value="groups" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-purple-600" />
                    Group Management
                  </CardTitle>
                  <CardDescription>
                    Manage groups/workspaces and their members
                  </CardDescription>
                </div>
                <Dialog open={showCreateGroup} onOpenChange={setShowCreateGroup}>
                  <DialogTrigger asChild>
                    <Button className="bg-purple-600 hover:bg-purple-700" data-testid="create-group-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Create Group
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Create New Group</DialogTitle>
                      <DialogDescription>
                        Create a new workspace for a team or committee
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label>Group Name</Label>
                        <Input 
                          placeholder="e.g., Finance Committee"
                          value={newGroup.name}
                          onChange={(e) => setNewGroup({...newGroup, name: e.target.value})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Short Name</Label>
                        <Input 
                          placeholder="e.g., Finance"
                          value={newGroup.short_name}
                          onChange={(e) => setNewGroup({...newGroup, short_name: e.target.value})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Action Prefix (2-5 uppercase letters)</Label>
                        <Input 
                          placeholder="e.g., FIN"
                          value={newGroup.prefix}
                          onChange={(e) => setNewGroup({...newGroup, prefix: e.target.value.toUpperCase()})}
                          maxLength={5}
                        />
                        <p className="text-xs text-gray-500">Actions in this group will be numbered {newGroup.prefix || 'XXX'}-0001, {newGroup.prefix || 'XXX'}-0002, etc.</p>
                      </div>
                      <div className="space-y-2">
                        <Label>Description</Label>
                        <Input 
                          placeholder="Brief description of the group's purpose"
                          value={newGroup.description}
                          onChange={(e) => setNewGroup({...newGroup, description: e.target.value})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Group Colour</Label>
                        <div className="flex gap-2">
                          <input 
                            type="color" 
                            value={newGroup.color}
                            onChange={(e) => setNewGroup({...newGroup, color: e.target.value})}
                            className="w-12 h-10 rounded cursor-pointer"
                          />
                          <Input 
                            value={newGroup.color}
                            onChange={(e) => setNewGroup({...newGroup, color: e.target.value})}
                            className="w-28"
                          />
                        </div>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setShowCreateGroup(false)}>Cancel</Button>
                      <Button 
                        onClick={handleCreateGroup}
                        disabled={!newGroup.name || !newGroup.short_name || !newGroup.prefix || newGroup.prefix.length < 2}
                        className="bg-purple-600 hover:bg-purple-700"
                      >
                        Create Group
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {groupsLoading ? (
                <div className="text-center py-8 text-gray-500">Loading groups...</div>
              ) : (
                <div className="space-y-4">
                  {groups.map((group) => (
                    <div 
                      key={group.id}
                      className="border rounded-lg p-4 hover:border-purple-300 transition-colors"
                      data-testid={`group-card-admin-${group.prefix}`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold"
                            style={{ backgroundColor: group.settings?.color || '#3B82F6' }}
                          >
                            {group.short_name?.charAt(0) || group.name?.charAt(0)}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-gray-900">{group.name}</span>
                              <Badge variant="outline">{group.prefix}</Badge>
                              {group.is_default && (
                                <Badge className="bg-blue-100 text-blue-800">Default</Badge>
                              )}
                            </div>
                            <p className="text-sm text-gray-500">{group.description || 'No description'}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => {
                              setSelectedGroup(group);
                              setShowEditGroup(true);
                            }}
                          >
                            <Edit className="w-4 h-4 mr-1" />
                            Edit
                          </Button>
                          {!group.is_default && (
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="text-red-600 hover:bg-red-50"
                              onClick={() => handleArchiveGroup(group.id, group.name)}
                            >
                              <Archive className="w-4 h-4 mr-1" />
                              Archive
                            </Button>
                          )}
                        </div>
                      </div>
                      
                      {/* Members Section */}
                      <Collapsible onOpenChange={(open) => open && fetchGroupMembers(group.id)}>
                        <CollapsibleTrigger asChild>
                          <Button variant="ghost" size="sm" className="w-full justify-between">
                            <span className="flex items-center gap-2">
                              <Users className="w-4 h-4" />
                              Members
                              <Badge 
                                variant="secondary" 
                                className={`ml-1 ${
                                  (groupMembers[group.id]?.length || 0) === 0 
                                    ? 'bg-gray-200 text-gray-600' 
                                    : 'bg-blue-100 text-blue-700'
                                }`}
                              >
                                {groupMembers[group.id]?.length || 0}
                              </Badge>
                            </span>
                            <ChevronDown className="w-4 h-4" />
                          </Button>
                        </CollapsibleTrigger>
                        <CollapsibleContent className="mt-2">
                          <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                            <div className="flex justify-between items-center mb-2">
                              <span className="text-sm font-medium text-gray-700">
                                {groupMembers[group.id]?.length || 0} members
                              </span>
                              <Dialog open={showAddMember && selectedGroup?.id === group.id} onOpenChange={(open) => {
                                setShowAddMember(open);
                                if (open) {
                                  setSelectedGroup(group);
                                  setAddMemberMode('existing');
                                  setNewUserForGroup({ name: '', email: '', username: '', phone: '', group_role: 'member', send_invitation: true });
                                }
                              }}>
                                <DialogTrigger asChild>
                                  <Button size="sm" variant="outline">
                                    <Plus className="w-3 h-3 mr-1" />
                                    Add Member
                                  </Button>
                                </DialogTrigger>
                                <DialogContent className="max-w-md">
                                  <DialogHeader>
                                    <DialogTitle>Add Member to {group.name}</DialogTitle>
                                  </DialogHeader>
                                  
                                  {/* Mode Toggle */}
                                  <div className="flex border rounded-lg p-1 bg-gray-100">
                                    <button
                                      className={`flex-1 py-2 px-3 rounded text-sm font-medium transition-colors ${
                                        addMemberMode === 'existing' ? 'bg-white shadow text-blue-600' : 'text-gray-600 hover:text-gray-800'
                                      }`}
                                      onClick={() => setAddMemberMode('existing')}
                                    >
                                      Select Existing User
                                    </button>
                                    <button
                                      className={`flex-1 py-2 px-3 rounded text-sm font-medium transition-colors ${
                                        addMemberMode === 'create' ? 'bg-white shadow text-blue-600' : 'text-gray-600 hover:text-gray-800'
                                      }`}
                                      onClick={() => setAddMemberMode('create')}
                                    >
                                      Create New User
                                    </button>
                                  </div>
                                  
                                  <div className="space-y-4 py-2">
                                    {addMemberMode === 'existing' ? (
                                      /* Existing User Selection */
                                      <div className="space-y-2">
                                        <Label>Select User</Label>
                                        <Select onValueChange={(value) => {
                                          const [userId, role] = value.split('|');
                                          handleAddMember(group.id, userId, role || 'member');
                                        }}>
                                          <SelectTrigger>
                                            <SelectValue placeholder="Choose a user..." />
                                          </SelectTrigger>
                                          <SelectContent>
                                            {allUsers
                                              .filter(u => !groupMembers[group.id]?.some(m => m.user_id === u.id))
                                              .map(u => (
                                                <SelectItem key={u.id} value={`${u.id}|member`}>
                                                  {u.name} ({u.role})
                                                </SelectItem>
                                              ))}
                                          </SelectContent>
                                        </Select>
                                        {allUsers.filter(u => !groupMembers[group.id]?.some(m => m.user_id === u.id)).length === 0 && (
                                          <p className="text-sm text-gray-500 italic">All users are already members of this group</p>
                                        )}
                                      </div>
                                    ) : (
                                      /* Create New User Form */
                                      <div className="space-y-3">
                                        <div className="grid grid-cols-2 gap-3">
                                          <div className="space-y-1">
                                            <Label className="text-xs">Full Name *</Label>
                                            <Input
                                              placeholder="John Smith"
                                              value={newUserForGroup.name}
                                              onChange={(e) => setNewUserForGroup({...newUserForGroup, name: e.target.value})}
                                            />
                                          </div>
                                          <div className="space-y-1">
                                            <Label className="text-xs">Username *</Label>
                                            <Input
                                              placeholder="jsmith"
                                              value={newUserForGroup.username}
                                              onChange={(e) => setNewUserForGroup({...newUserForGroup, username: e.target.value.toLowerCase().replace(/\s/g, '')})}
                                            />
                                          </div>
                                        </div>
                                        <div className="space-y-1">
                                          <Label className="text-xs">Email *</Label>
                                          <Input
                                            type="email"
                                            placeholder="john.smith@email.com"
                                            value={newUserForGroup.email}
                                            onChange={(e) => setNewUserForGroup({...newUserForGroup, email: e.target.value})}
                                          />
                                        </div>
                                        <div className="grid grid-cols-2 gap-3">
                                          <div className="space-y-1">
                                            <Label className="text-xs">Phone</Label>
                                            <Input
                                              placeholder="07xxx xxx xxx"
                                              value={newUserForGroup.phone}
                                              onChange={(e) => setNewUserForGroup({...newUserForGroup, phone: e.target.value})}
                                            />
                                          </div>
                                          <div className="space-y-1">
                                            <Label className="text-xs">Group Role *</Label>
                                            <Select 
                                              value={newUserForGroup.group_role}
                                              onValueChange={(value) => setNewUserForGroup({...newUserForGroup, group_role: value})}
                                            >
                                              <SelectTrigger>
                                                <SelectValue />
                                              </SelectTrigger>
                                              <SelectContent>
                                                <SelectItem value="admin">Admin</SelectItem>
                                                <SelectItem value="member">Member</SelectItem>
                                                <SelectItem value="viewer">Viewer</SelectItem>
                                                <SelectItem value="external">External</SelectItem>
                                              </SelectContent>
                                            </Select>
                                          </div>
                                        </div>
                                        <div className="flex items-center gap-2 pt-2">
                                          <input
                                            type="checkbox"
                                            id="send-invite"
                                            checked={newUserForGroup.send_invitation}
                                            onChange={(e) => setNewUserForGroup({...newUserForGroup, send_invitation: e.target.checked})}
                                            className="rounded border-gray-300"
                                          />
                                          <Label htmlFor="send-invite" className="text-sm cursor-pointer">
                                            Send invitation email now
                                          </Label>
                                        </div>
                                        <Button 
                                          className="w-full mt-4 bg-blue-600 hover:bg-blue-700 text-white"
                                          onClick={() => handleCreateUserForGroup(group.id)}
                                          disabled={!newUserForGroup.name || !newUserForGroup.email || !newUserForGroup.username}
                                        >
                                          <UserPlus className="w-4 h-4 mr-2" />
                                          Create User & Add to Group
                                        </Button>
                                      </div>
                                    )}
                                  </div>
                                </DialogContent>
                              </Dialog>
                            </div>
                            {groupMembers[group.id]?.map((member) => (
                              <div key={member.id} className="flex items-center justify-between py-2 px-3 bg-white rounded border">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-sm">{member.user?.name}</span>
                                  <Badge variant="outline" className="text-xs">
                                    {member.role}
                                  </Badge>
                                </div>
                                <div className="flex items-center gap-1">
                                  <Select 
                                    value={member.role}
                                    onValueChange={(newRole) => handleUpdateMemberRole(group.id, member.user_id, newRole)}
                                  >
                                    <SelectTrigger className="w-24 h-7 text-xs">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="admin">Admin</SelectItem>
                                      <SelectItem value="member">Member</SelectItem>
                                      <SelectItem value="viewer">Viewer</SelectItem>
                                      <SelectItem value="external">External</SelectItem>
                                    </SelectContent>
                                  </Select>
                                  <Button 
                                    variant="ghost" 
                                    size="sm"
                                    className="h-7 w-7 p-0 text-red-600 hover:bg-red-50"
                                    onClick={() => handleRemoveMember(group.id, member.user_id, member.user?.name)}
                                  >
                                    <UserX className="w-3 h-3" />
                                  </Button>
                                </div>
                              </div>
                            ))}
                            {(!groupMembers[group.id] || groupMembers[group.id].length === 0) && (
                              <p className="text-sm text-gray-500 text-center py-2">No members loaded. Click to expand.</p>
                            )}
                          </div>
                        </CollapsibleContent>
                      </Collapsible>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Edit Group Dialog */}
          <Dialog open={showEditGroup} onOpenChange={setShowEditGroup}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Edit Group</DialogTitle>
              </DialogHeader>
              {selectedGroup && (
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>Group Name</Label>
                    <Input 
                      value={selectedGroup.name}
                      onChange={(e) => setSelectedGroup({...selectedGroup, name: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Short Name</Label>
                    <Input 
                      value={selectedGroup.short_name}
                      onChange={(e) => setSelectedGroup({...selectedGroup, short_name: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Input 
                      value={selectedGroup.description || ''}
                      onChange={(e) => setSelectedGroup({...selectedGroup, description: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Group Colour</Label>
                    <div className="flex gap-2">
                      <input 
                        type="color" 
                        value={selectedGroup.settings?.color || '#3B82F6'}
                        onChange={(e) => setSelectedGroup({
                          ...selectedGroup, 
                          settings: {...selectedGroup.settings, color: e.target.value}
                        })}
                        className="w-12 h-10 rounded cursor-pointer"
                      />
                      <Input 
                        value={selectedGroup.settings?.color || '#3B82F6'}
                        onChange={(e) => setSelectedGroup({
                          ...selectedGroup, 
                          settings: {...selectedGroup.settings, color: e.target.value}
                        })}
                        className="w-28"
                      />
                    </div>
                  </div>
                  <p className="text-sm text-gray-500">
                    Prefix: <strong>{selectedGroup.prefix}</strong> (cannot be changed)
                  </p>
                </div>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowEditGroup(false)}>Cancel</Button>
                <Button onClick={handleUpdateGroup} className="bg-purple-600 hover:bg-purple-700">
                  Save Changes
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Active Sessions Tab */}
        <TabsContent value="sessions" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-green-600" />
                    Active Sessions Dashboard
                  </CardTitle>
                  <CardDescription>
                    Monitor and manage user sessions across the platform
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    onClick={fetchSessionsData}
                    disabled={sessionsLoading}
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${sessionsLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button 
                        variant="outline"
                        className="text-orange-600 hover:bg-orange-50"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Cleanup Sessions
                        <ChevronDown className="w-4 h-4 ml-1" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={handleCleanupExpiredSessions}>
                        Cleanup Expired Only
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => handleCleanupOldSessions(1)}>
                        Older than 1 day
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleCleanupOldSessions(3)}>
                        Older than 3 days
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleCleanupOldSessions(7)}>
                        Older than 7 days
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Session Info Banner */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 text-sm text-blue-800">
                <strong>Session Lifetime:</strong> Sessions expire after <strong>7 days</strong> of inactivity. 
                Each user can have up to 3 active sessions (older ones are auto-cleaned on login).
              </div>
              
              {/* Session Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-green-600">{sessions.stats?.active_sessions || 0}</div>
                  <p className="text-sm text-gray-600">Active Sessions</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-blue-600">{sessions.stats?.unique_active_users || 0}</div>
                  <p className="text-sm text-gray-600">Unique Users</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-gray-600">{sessions.stats?.expired_sessions || 0}</div>
                  <p className="text-sm text-gray-600">Expired Sessions</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-purple-600">{sessions.stats?.total_sessions || 0}</div>
                  <p className="text-sm text-gray-600">Total Sessions</p>
                </div>
              </div>

              {/* Sessions by Role */}
              {sessions.stats?.by_role && Object.keys(sessions.stats.by_role).length > 0 && (
                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-medium text-gray-700 mb-2">Active Sessions by Role</h4>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(sessions.stats.by_role).map(([role, count]) => (
                      <Badge key={role} variant="outline" className="text-sm">
                        {role.replace('_', ' ')}: {count}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Sessions Table */}
              {sessionsLoading ? (
                <div className="text-center py-8 text-gray-500">Loading sessions...</div>
              ) : (
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Login Time</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expires</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {sessions.sessions?.slice(0, 50).map((session, idx) => (
                        <tr key={idx} className={session.is_expired ? 'bg-gray-50 opacity-60' : ''}>
                          <td className="px-4 py-3">
                            <div>
                              <div className="font-medium text-gray-900">{session.user_name}</div>
                              <div className="text-xs text-gray-500">{session.user_email}</div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <Badge variant="outline" className="text-xs capitalize">
                              {session.user_role?.replace('_', ' ')}
                            </Badge>
                          </td>
                          <td className="px-4 py-3">
                            {session.is_active ? (
                              <Badge className="bg-green-100 text-green-800">Active</Badge>
                            ) : (
                              <Badge className="bg-gray-100 text-gray-600">Expired</Badge>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {new Date(session.created_at).toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {new Date(session.expires_at).toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex justify-end gap-1">
                              {session.is_active && (
                                <>
                                  <Button 
                                    variant="ghost" 
                                    size="sm"
                                    className="text-red-600 hover:bg-red-50"
                                    onClick={() => handleTerminateSession(session.full_token, session.user_name)}
                                    title="Terminate this session"
                                  >
                                    <LogOut className="w-4 h-4" />
                                  </Button>
                                  <Button 
                                    variant="ghost" 
                                    size="sm"
                                    className="text-orange-600 hover:bg-orange-50"
                                    onClick={() => handleTerminateUserSessions(session.user_id, session.user_name)}
                                    title="Terminate all sessions for this user"
                                  >
                                    <UserX className="w-4 h-4" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {sessions.sessions?.length > 50 && (
                    <div className="px-4 py-2 bg-gray-50 text-sm text-gray-500 text-center">
                      Showing 50 of {sessions.sessions.length} sessions
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Permission Audit Tab */}
        <TabsContent value="permissions" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <ShieldAlert className="w-5 h-5 text-orange-600" />
                    Permission Denials
                  </CardTitle>
                  <CardDescription>
                    Track when users are blocked from actions due to insufficient permissions
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={fetchPermissionAuditData}
                    disabled={permissionAuditLoading}
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${permissionAuditLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleCleanupPermissionAudit(30)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Cleanup 30d+
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Stats Cards */}
              {permissionAudit.stats && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                    <div className="flex items-center gap-2">
                      <Ban className="w-5 h-5 text-orange-600" />
                      <span className="text-sm text-gray-600">Total Denials</span>
                    </div>
                    <p className="text-2xl font-bold text-orange-700 mt-1">
                      {permissionAudit.stats.total_denials || 0}
                    </p>
                  </div>
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex items-center gap-2">
                      <Clock className="w-5 h-5 text-yellow-600" />
                      <span className="text-sm text-gray-600">Today</span>
                    </div>
                    <p className="text-2xl font-bold text-yellow-700 mt-1">
                      {permissionAudit.stats.today_denials || 0}
                    </p>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-blue-600" />
                      <span className="text-sm text-gray-600">Last 7 Days</span>
                    </div>
                    <p className="text-2xl font-bold text-blue-700 mt-1">
                      {permissionAudit.stats.week_denials || 0}
                    </p>
                  </div>
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <div className="flex items-center gap-2">
                      <Users className="w-5 h-5 text-purple-600" />
                      <span className="text-sm text-gray-600">Users Affected</span>
                    </div>
                    <p className="text-2xl font-bold text-purple-700 mt-1">
                      {permissionAudit.stats.by_user?.length || 0}
                    </p>
                  </div>
                </div>
              )}

              {/* Breakdown by Permission Type */}
              {permissionAudit.stats?.by_permission?.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
                      <Shield className="w-4 h-4" /> By Permission Type
                    </h4>
                    <div className="space-y-2">
                      {permissionAudit.stats.by_permission.map((item, idx) => (
                        <div key={idx} className="flex justify-between items-center">
                          <Badge variant="outline">{item.permission}</Badge>
                          <span className="text-sm font-medium">{item.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
                      <FileText className="w-4 h-4" /> By Resource Type
                    </h4>
                    <div className="space-y-2">
                      {permissionAudit.stats.by_resource?.map((item, idx) => (
                        <div key={idx} className="flex justify-between items-center">
                          <Badge variant="secondary">{item.resource || 'unknown'}</Badge>
                          <span className="text-sm font-medium">{item.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Top Users with Denials */}
              {permissionAudit.stats?.by_user?.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h4 className="font-medium text-red-700 mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" /> Users with Most Permission Denials
                  </h4>
                  <div className="space-y-2">
                    {permissionAudit.stats.by_user.slice(0, 5).map((item, idx) => (
                      <div key={idx} className="flex justify-between items-center bg-white rounded px-3 py-2">
                        <span className="text-sm">{item.user_name}</span>
                        <Badge variant="destructive">{item.count} denied</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Denial Logs Table */}
              <div>
                <h4 className="font-medium text-gray-700 mb-3">
                  Recent Permission Denials ({permissionAudit.total || 0} total)
                </h4>
                
                {permissionAuditLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
                  </div>
                ) : permissionAudit.logs?.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-2" />
                    <p>No permission denials recorded</p>
                    <p className="text-sm">Users are operating within their access levels</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-gray-50">
                          <th className="text-left py-2 px-3">Timestamp</th>
                          <th className="text-left py-2 px-3">User</th>
                          <th className="text-left py-2 px-3">Group</th>
                          <th className="text-left py-2 px-3">Permission</th>
                          <th className="text-left py-2 px-3">Resource</th>
                          <th className="text-left py-2 px-3">Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {permissionAudit.logs.slice(0, 50).map((log, idx) => (
                          <tr key={idx} className="border-b hover:bg-gray-50">
                            <td className="py-2 px-3 text-gray-500">
                              {new Date(log.timestamp).toLocaleString()}
                            </td>
                            <td className="py-2 px-3">
                              <div>
                                <span className="font-medium">{log.user_name}</span>
                                <span className="text-gray-400 text-xs ml-1">({log.group_role || 'no role'})</span>
                              </div>
                            </td>
                            <td className="py-2 px-3">
                              <Badge variant="outline" className="text-xs">
                                {log.group_name || 'N/A'}
                              </Badge>
                            </td>
                            <td className="py-2 px-3">
                              <Badge 
                                variant={log.required_permission === 'delete' ? 'destructive' : 'secondary'}
                              >
                                {log.required_permission}
                              </Badge>
                            </td>
                            <td className="py-2 px-3 text-gray-600">
                              {log.resource_type}
                              {log.resource_id && (
                                <span className="text-gray-400 text-xs ml-1">#{log.resource_id.slice(0,8)}</span>
                              )}
                            </td>
                            <td className="py-2 px-3 text-gray-600 max-w-xs truncate" title={log.denial_reason}>
                              {log.denial_reason}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Backup & Restore Tab */}
        <TabsContent value="backup" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Download className="w-5 h-5 text-blue-600" />
                  Create Backup
                </CardTitle>
                <CardDescription>
                  Download a complete backup of the database as JSON
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button 
                  onClick={handleCreateBackup} 
                  className="w-full bg-gradient-to-r from-blue-500 to-green-500"
                  data-testid="create-backup-button"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Create & Download Backup
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Upload className="w-5 h-5 text-orange-600" />
                  Restore Backup
                </CardTitle>
                <CardDescription>
                  Restore database from a previously downloaded backup file
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Dialog open={showRestoreDialog} onOpenChange={setShowRestoreDialog}>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="w-full border-orange-300 text-orange-600 hover:bg-orange-50">
                      <Upload className="w-4 h-4 mr-2" />
                      Restore from Backup
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle className="text-red-600">⚠️ Restore Database</DialogTitle>
                      <DialogDescription>
                        This will replace ALL current data with the backup data. This action cannot be undone.
                        Make sure to create a backup of current data first.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label htmlFor="backup-file">Select Backup File (JSON)</Label>
                        <Input
                          id="backup-file"
                          type="file"
                          accept=".json"
                          onChange={(e) => setRestoreFile(e.target.files?.[0])}
                          data-testid="restore-file-input"
                        />
                      </div>
                      {restoreFile && (
                        <p className="text-sm text-green-600">
                          Selected: {restoreFile.name}
                        </p>
                      )}
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setShowRestoreDialog(false)}>
                        Cancel
                      </Button>
                      <Button 
                        variant="destructive" 
                        onClick={handleRestoreBackup}
                        disabled={!restoreFile}
                        data-testid="confirm-restore-button"
                      >
                        Restore Database
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </CardContent>
            </Card>
          </div>

          {/* Backup History */}
          <Card>
            <CardHeader>
              <CardTitle>Backup History</CardTitle>
            </CardHeader>
            <CardContent>
              {backups.length === 0 ? (
                <p className="text-center text-gray-500 py-4">No backups created yet</p>
              ) : (
                <div className="space-y-2">
                  {backups.map((backup) => (
                    <div key={backup.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <p className="font-medium">{backup.name}</p>
                        <p className="text-sm text-gray-500">
                          Created by {backup.created_by_name} on {formatDate(backup.created_at)}
                        </p>
                      </div>
                      <div className="text-sm text-gray-500">
                        {Object.values(backup.record_counts || {}).reduce((a, b) => a + b, 0)} records
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Database Health Tab */}
        <TabsContent value="health" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <HeartPulse className="w-5 h-5 text-blue-600" />
                Database Health Check
              </CardTitle>
              <CardDescription>
                Scan for duplicate records, orphan references, and data integrity issues
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button 
                onClick={runDatabaseHealthCheck} 
                disabled={healthLoading}
                className="bg-gradient-to-r from-blue-500 to-green-500"
                data-testid="run-health-check-button"
              >
                {healthLoading ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Scanning...
                  </>
                ) : (
                  <>
                    <Search className="w-4 h-4 mr-2" />
                    Run Health Check
                  </>
                )}
              </Button>

              {dbHealth && (
                <div className="space-y-4 mt-4">
                  {/* Health Status Banner */}
                  <div className={`p-4 rounded-lg border ${
                    dbHealth.health_status === 'HEALTHY' 
                      ? 'bg-green-50 border-green-200' 
                      : dbHealth.health_status === 'WARNINGS'
                      ? 'bg-yellow-50 border-yellow-200'
                      : 'bg-red-50 border-red-200'
                  }`}>
                    <div className="flex items-center gap-2">
                      {dbHealth.health_status === 'HEALTHY' ? (
                        <CheckCircle className="w-6 h-6 text-green-600" />
                      ) : dbHealth.health_status === 'WARNINGS' ? (
                        <AlertTriangle className="w-6 h-6 text-yellow-600" />
                      ) : (
                        <AlertCircle className="w-6 h-6 text-red-600" />
                      )}
                      <div>
                        <p className={`font-semibold ${
                          dbHealth.health_status === 'HEALTHY' ? 'text-green-700' :
                          dbHealth.health_status === 'WARNINGS' ? 'text-yellow-700' : 'text-red-700'
                        }`}>
                          {dbHealth.health_status === 'HEALTHY' ? 'Database is Healthy' :
                           dbHealth.health_status === 'WARNINGS' ? 'Database has Warnings' : 'Issues Found'}
                        </p>
                        <p className="text-sm text-gray-600">
                          Checked: {new Date(dbHealth.checked_at).toLocaleString('en-GB')}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-600">Actions</p>
                      <p className="text-xl font-bold">{dbHealth.stats.total_actions}</p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-600">Users</p>
                      <p className="text-xl font-bold">{dbHealth.stats.users}</p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-600">Categories</p>
                      <p className="text-xl font-bold">{dbHealth.stats.categories}</p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-600">Notes</p>
                      <p className="text-xl font-bold">{dbHealth.stats.total_notes}</p>
                    </div>
                  </div>

                  {/* Issues Section */}
                  {dbHealth.issues.length > 0 && (
                    <div className="space-y-3">
                      <h4 className="font-semibold text-red-700 flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" />
                        Issues ({dbHealth.total_issues})
                      </h4>
                      {dbHealth.issues.map((issue, idx) => (
                        <div key={idx} className="p-4 bg-red-50 border border-red-200 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium text-red-700">{issue.type.replace(/_/g, ' ')}</span>
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              issue.severity === 'HIGH' ? 'bg-red-200 text-red-800' :
                              issue.severity === 'MEDIUM' ? 'bg-orange-200 text-orange-800' :
                              'bg-yellow-200 text-yellow-800'
                            }`}>
                              {issue.severity}
                            </span>
                          </div>
                          <p className="text-sm text-red-600 mb-2">
                            {issue.description || `${issue.count} record(s) affected`}
                          </p>
                          {issue.details && issue.details.length > 0 && (
                            <div className="text-xs text-gray-600 bg-white p-2 rounded max-h-32 overflow-y-auto">
                              {issue.details.slice(0, 5).map((d, i) => (
                                <div key={i} className="mb-1">
                                  {d.reference && <span className="font-mono">{d.reference}</span>}
                                  {d.title && <span className="ml-2">{d.title}</span>}
                                  {d.invalid_owner_id && <span className="text-red-500 ml-2">Invalid owner: {d.invalid_owner_id.substring(0,8)}...</span>}
                                  {d.invalid_category_id && <span className="text-red-500 ml-2">Invalid category: {d.invalid_category_id.substring(0,8)}...</span>}
                                </div>
                              ))}
                              {issue.details.length > 5 && <p className="text-gray-400">...and {issue.details.length - 5} more</p>}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Warnings Section */}
                  {dbHealth.warnings.length > 0 && (
                    <div className="space-y-3">
                      <h4 className="font-semibold text-yellow-700 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" />
                        Warnings ({dbHealth.total_warnings})
                      </h4>
                      {dbHealth.warnings.map((warning, idx) => (
                        <div key={idx} className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium text-yellow-700">{warning.type.replace(/_/g, ' ')}</span>
                            <span className="px-2 py-0.5 rounded text-xs font-medium bg-yellow-200 text-yellow-800">
                              {warning.severity}
                            </span>
                          </div>
                          <p className="text-sm text-yellow-600">
                            {warning.description || `${warning.count} record(s) affected`}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Cleanup Tools Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Wrench className="w-5 h-5 text-orange-600" />
                Non-Destructive Cleanup Tools
              </CardTitle>
              <CardDescription>
                Safe cleanup operations that fix orphan references without deleting core data
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Clear Invalid Owners */}
                <div className="p-4 border rounded-lg">
                  <h5 className="font-medium mb-2">Clear Invalid Owner References</h5>
                  <p className="text-sm text-gray-600 mb-3">
                    Actions referencing deleted users will have owner cleared (not deleted)
                  </p>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => runCleanup('clear_invalid_owners', true)}
                      disabled={cleanupLoading}
                    >
                      Preview
                    </Button>
                    <Dialog open={cleanupDialogs.owners} onOpenChange={(open) => setCleanupDialogs(prev => ({ ...prev, owners: open }))}>
                      <DialogTrigger asChild>
                        <Button 
                          variant="default" 
                          size="sm"
                          className="bg-orange-500 hover:bg-orange-600"
                          disabled={cleanupLoading}
                        >
                          Run Cleanup
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Confirm Cleanup</DialogTitle>
                          <DialogDescription>
                            This will clear owner_id on actions that reference non-existent users. 
                            The actions themselves will NOT be deleted.
                          </DialogDescription>
                        </DialogHeader>
                        <DialogFooter>
                          <Button variant="outline" onClick={() => setCleanupDialogs(prev => ({ ...prev, owners: false }))}>Cancel</Button>
                          <Button 
                            className="bg-orange-500 hover:bg-orange-600"
                            onClick={() => runCleanup('clear_invalid_owners', false, 'owners')}
                          >
                            Confirm Cleanup
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>

                {/* Clear Invalid Categories */}
                <div className="p-4 border rounded-lg">
                  <h5 className="font-medium mb-2">Clear Invalid Category References</h5>
                  <p className="text-sm text-gray-600 mb-3">
                    Actions referencing deleted categories will have category cleared
                  </p>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => runCleanup('clear_invalid_categories', true)}
                      disabled={cleanupLoading}
                    >
                      Preview
                    </Button>
                    <Dialog open={cleanupDialogs.categories} onOpenChange={(open) => setCleanupDialogs(prev => ({ ...prev, categories: open }))}>
                      <DialogTrigger asChild>
                        <Button 
                          variant="default" 
                          size="sm"
                          className="bg-orange-500 hover:bg-orange-600"
                          disabled={cleanupLoading}
                        >
                          Run Cleanup
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Confirm Cleanup</DialogTitle>
                          <DialogDescription>
                            This will clear category on actions that reference non-existent categories.
                          </DialogDescription>
                        </DialogHeader>
                        <DialogFooter>
                          <Button variant="outline" onClick={() => setCleanupDialogs(prev => ({ ...prev, categories: false }))}>Cancel</Button>
                          <Button 
                            className="bg-orange-500 hover:bg-orange-600"
                            onClick={() => runCleanup('clear_invalid_categories', false, 'categories')}
                          >
                            Confirm Cleanup
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>

                {/* Clear Invalid Meeting Links */}
                <div className="p-4 border rounded-lg">
                  <h5 className="font-medium mb-2">Clear Invalid Meeting Links</h5>
                  <p className="text-sm text-gray-600 mb-3">
                    Actions linked to deleted meetings will have the link removed
                  </p>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => runCleanup('clear_invalid_meeting_links', true)}
                      disabled={cleanupLoading}
                    >
                      Preview
                    </Button>
                    <Dialog open={cleanupDialogs.meetings} onOpenChange={(open) => setCleanupDialogs(prev => ({ ...prev, meetings: open }))}>
                      <DialogTrigger asChild>
                        <Button 
                          variant="default" 
                          size="sm"
                          className="bg-orange-500 hover:bg-orange-600"
                          disabled={cleanupLoading}
                        >
                          Run Cleanup
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Confirm Cleanup</DialogTitle>
                          <DialogDescription>
                            This will clear meeting_id on actions that link to non-existent meetings.
                          </DialogDescription>
                        </DialogHeader>
                        <DialogFooter>
                          <Button variant="outline" onClick={() => setCleanupDialogs(prev => ({ ...prev, meetings: false }))}>Cancel</Button>
                          <Button 
                            className="bg-orange-500 hover:bg-orange-600"
                            onClick={() => runCleanup('clear_invalid_meeting_links', false, 'meetings')}
                          >
                            Confirm Cleanup
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>

                {/* Delete Orphan Notes */}
                <div className="p-4 border rounded-lg">
                  <h5 className="font-medium mb-2">Delete Orphan Notes</h5>
                  <p className="text-sm text-gray-600 mb-3">
                    Delete notes that reference non-existent actions
                  </p>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => runCleanup('delete_orphan_notes', true)}
                      disabled={cleanupLoading}
                    >
                      Preview
                    </Button>
                    <Dialog open={cleanupDialogs.notes} onOpenChange={(open) => setCleanupDialogs(prev => ({ ...prev, notes: open }))}>
                      <DialogTrigger asChild>
                        <Button 
                          variant="destructive" 
                          size="sm"
                          disabled={cleanupLoading}
                        >
                          Delete Orphans
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle className="text-red-600">⚠️ Delete Orphan Notes</DialogTitle>
                          <DialogDescription>
                            This will permanently delete notes that reference non-existent actions. 
                            This cannot be undone.
                          </DialogDescription>
                        </DialogHeader>
                        <DialogFooter>
                          <Button variant="outline" onClick={() => setCleanupDialogs(prev => ({ ...prev, notes: false }))}>Cancel</Button>
                          <Button 
                            variant="destructive"
                            onClick={() => runCleanup('delete_orphan_notes', false, 'notes')}
                          >
                            Delete Permanently
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>

                {/* Delete Orphan Agenda Items */}
                <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                  <h4 className="font-medium text-red-800 mb-2">🗓️ Delete Orphan Agenda Items</h4>
                  <p className="text-sm text-gray-600 mb-3">
                    Delete agenda items that reference non-existent meetings
                  </p>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => runCleanup('delete_orphan_agenda_items', true)}
                      disabled={cleanupLoading}
                    >
                      Preview
                    </Button>
                    <Dialog open={cleanupDialogs.agendaItems} onOpenChange={(open) => setCleanupDialogs(prev => ({ ...prev, agendaItems: open }))}>
                      <DialogTrigger asChild>
                        <Button 
                          variant="destructive" 
                          size="sm"
                          disabled={cleanupLoading}
                        >
                          Delete Orphans
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle className="text-red-600">⚠️ Delete Orphan Agenda Items</DialogTitle>
                          <DialogDescription>
                            This will permanently delete agenda items that reference non-existent meetings. 
                            This cannot be undone.
                          </DialogDescription>
                        </DialogHeader>
                        <DialogFooter>
                          <Button variant="outline" onClick={() => setCleanupDialogs(prev => ({ ...prev, agendaItems: false }))}>Cancel</Button>
                          <Button 
                            variant="destructive"
                            onClick={() => runCleanup('delete_orphan_agenda_items', false, 'agendaItems')}
                          >
                            Delete Permanently
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>

                {/* Delete Orphan Org Reports */}
                <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                  <h4 className="font-medium text-red-800 mb-2">📋 Delete Orphan Org Reports</h4>
                  <p className="text-sm text-gray-600 mb-3">
                    Delete organization reports that reference non-existent orgs or meetings
                  </p>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => runCleanup('delete_orphan_org_reports', true)}
                      disabled={cleanupLoading}
                    >
                      Preview
                    </Button>
                    <Dialog open={cleanupDialogs.orgReports} onOpenChange={(open) => setCleanupDialogs(prev => ({ ...prev, orgReports: open }))}>
                      <DialogTrigger asChild>
                        <Button 
                          variant="destructive" 
                          size="sm"
                          disabled={cleanupLoading}
                        >
                          Delete Orphans
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle className="text-red-600">⚠️ Delete Orphan Org Reports</DialogTitle>
                          <DialogDescription>
                            This will permanently delete organization reports that reference non-existent organizations or meetings. 
                            This cannot be undone.
                          </DialogDescription>
                        </DialogHeader>
                        <DialogFooter>
                          <Button variant="outline" onClick={() => setCleanupDialogs(prev => ({ ...prev, orgReports: false }))}>Cancel</Button>
                          <Button 
                            variant="destructive"
                            onClick={() => runCleanup('delete_orphan_org_reports', false, 'orgReports')}
                          >
                            Delete Permanently
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
              </div>

              {/* Cleanup Results */}
              {cleanupResults && (
                <div className={`p-4 rounded-lg border ${cleanupResults.dry_run ? 'bg-blue-50 border-blue-200' : 'bg-green-50 border-green-200'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    {cleanupResults.dry_run ? (
                      <Search className="w-5 h-5 text-blue-600" />
                    ) : (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    )}
                    <span className={`font-semibold ${cleanupResults.dry_run ? 'text-blue-700' : 'text-green-700'}`}>
                      {cleanupResults.dry_run ? 'Preview Results' : 'Cleanup Complete'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700 mb-2">
                    <strong>Action:</strong> {cleanupResults.action}
                  </p>
                  <p className="text-sm text-gray-700 mb-2">
                    <strong>Records affected:</strong> {cleanupResults.count}
                  </p>
                  {cleanupResults.affected_records.length > 0 && (
                    <div className="text-xs bg-white p-2 rounded max-h-40 overflow-y-auto">
                      {cleanupResults.affected_records.slice(0, 10).map((record, i) => (
                        <div key={i} className="mb-1 font-mono">
                          {JSON.stringify(record)}
                        </div>
                      ))}
                      {cleanupResults.affected_records.length > 10 && (
                        <p className="text-gray-400">...and {cleanupResults.affected_records.length - 10} more</p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Bulk Delete Actions Card */}
          <Card className="border-red-300">
            <CardHeader className="bg-red-50">
              <CardTitle className="flex items-center gap-2 text-red-700">
                <Trash2 className="w-5 h-5" />
                Bulk Delete Actions
              </CardTitle>
              <CardDescription>
                Permanently delete all actions from the system. Use with extreme caution.
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                <p className="text-sm text-gray-700 mb-2">
                  <strong>Current active actions:</strong> {allActions.length}
                </p>
                <p className="text-sm text-red-600 mb-4">
                  ⚠️ This will permanently delete ALL non-archived actions, their notes, and audit logs. 
                  This cannot be undone.
                </p>
                <Dialog open={showBulkDeleteDialog} onOpenChange={setShowBulkDeleteDialog}>
                  <DialogTrigger asChild>
                    <Button 
                      variant="destructive"
                      disabled={allActions.length === 0 || bulkDeleteLoading}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete All {allActions.length} Actions
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle className="text-red-600">⚠️ DANGER: Delete All Actions</DialogTitle>
                      <DialogDescription>
                        You are about to permanently delete <strong>{allActions.length} actions</strong> and all their associated notes and audit logs.
                        <br /><br />
                        This action <strong>CANNOT be undone</strong>. Are you absolutely sure?
                      </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setShowBulkDeleteDialog(false)}>Cancel</Button>
                      <Button 
                        variant="destructive"
                        onClick={handleBulkDeleteAll}
                        disabled={bulkDeleteLoading}
                      >
                        {bulkDeleteLoading ? 'Deleting...' : 'Yes, Delete Everything'}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </CardContent>
          </Card>

          {/* Data Migrations Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="w-5 h-5 text-purple-600" />
                Data Migrations
              </CardTitle>
              <CardDescription>
                One-time data migrations to fix or update historical data
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Closed By Migration */}
              <div className="border rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-semibold text-gray-900">Migrate "Closed By" Field</h4>
                    <p className="text-sm text-gray-600 mt-1">
                      Backfill the "Closed By" field for historical closed actions by searching audit logs and completion notes.
                      This fixes actions showing "Unknown" in the Meeting Review Report.
                    </p>
                  </div>
                  <Button
                    onClick={runClosedByMigration}
                    disabled={migrationLoading}
                    className="bg-purple-600 hover:bg-purple-700 ml-4"
                    data-testid="run-closed-by-migration"
                  >
                    {migrationLoading ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Running...
                      </>
                    ) : (
                      <>
                        <Database className="w-4 h-4 mr-2" />
                        Run Migration
                      </>
                    )}
                  </Button>
                </div>

                {migrationResults && (
                  <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <p className="text-2xl font-bold text-gray-700">{migrationResults.total_closed_without_closer}</p>
                        <p className="text-xs text-gray-500">Actions Found</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-green-600">{migrationResults.migrated}</p>
                        <p className="text-xs text-gray-500">Successfully Updated</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-amber-600">{migrationResults.not_found}</p>
                        <p className="text-xs text-gray-500">No Data Found</p>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mt-3 text-center">{migrationResults.message}</p>
                  </div>
                )}
              </div>

              {/* Manual Closed By Assignment */}
              {actionsMissingClosedBy.length > 0 && (
                <div className="border rounded-lg p-4 mt-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Manual "Closed By" Assignment</h4>
                  <p className="text-sm text-gray-600 mb-4">
                    The following {actionsMissingClosedBy.length} action(s) have no audit trail. You can manually assign who closed them:
                  </p>
                  <div className="space-y-3">
                    {actionsMissingClosedBy.map(action => (
                      <div key={action.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <span className="font-mono text-sm font-semibold">{action.reference}</span>
                          <span className="text-gray-600 text-sm ml-2">{action.title?.substring(0, 40)}{action.title?.length > 40 ? '...' : ''}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <select
                            className="border rounded px-2 py-1 text-sm"
                            defaultValue=""
                            onChange={(e) => {
                              if (e.target.value) {
                                handleManualClosedBy(action.id, e.target.value);
                              }
                            }}
                            disabled={manualClosedByLoading}
                          >
                            <option value="">Select user...</option>
                            {availableUsers.map(user => (
                              <option key={user.id} value={user.id}>{user.name}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Email Notifications Tab */}
        <TabsContent value="notifications" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-blue-600" />
                Action Due Date Reminders
              </CardTitle>
              <CardDescription>
                Send email reminders to assignees for actions due within 7 days, 3 days, 1 day, or overdue.
                Each user receives ONE consolidated email with all their due actions.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="font-medium text-blue-800 mb-2">How it works:</h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• Actions due in <strong>7 days</strong> or less are included</li>
                  <li>• <strong>Overdue</strong> and <strong>Due Today</strong> actions are highlighted</li>
                  <li>• Each assignee receives <strong>one email</strong> with all their due actions</li>
                  <li>• Includes action details, category, and <strong>latest progress note</strong></li>
                  <li>• Direct link to view and update each action in the app</li>
                </ul>
              </div>

              <div className="flex gap-3">
                <Button 
                  variant="outline"
                  onClick={() => sendDueReminders(true)}
                  disabled={emailLoading}
                >
                  {emailLoading ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Search className="w-4 h-4 mr-2" />
                  )}
                  Preview (Dry Run)
                </Button>
                <Dialog open={showEmailReminderDialog} onOpenChange={setShowEmailReminderDialog}>
                  <DialogTrigger asChild>
                    <Button 
                      className="bg-blue-600 hover:bg-blue-700"
                      disabled={emailLoading}
                    >
                      <Send className="w-4 h-4 mr-2" />
                      Send Reminder Emails
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Send Email Reminders</DialogTitle>
                      <DialogDescription>
                        This will send reminder emails to all users with actions due within 7 days.
                        Each user will receive one consolidated email.
                      </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setShowEmailReminderDialog(false)}>Cancel</Button>
                      <Button 
                        className="bg-blue-600 hover:bg-blue-700"
                        onClick={() => sendDueReminders(false)}
                        disabled={emailLoading}
                      >
                        Confirm & Send
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>

              {/* Email Preview Results */}
              {emailReminderResults && (
                <div className={`p-4 rounded-lg border ${emailReminderResults.dry_run ? 'bg-blue-50 border-blue-200' : 'bg-green-50 border-green-200'}`}>
                  <div className="flex items-center gap-2 mb-3">
                    {emailReminderResults.dry_run ? (
                      <Search className="w-5 h-5 text-blue-600" />
                    ) : (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    )}
                    <span className={`font-semibold ${emailReminderResults.dry_run ? 'text-blue-700' : 'text-green-700'}`}>
                      {emailReminderResults.dry_run ? 'Preview Results' : 'Emails Sent'}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="p-3 bg-white rounded">
                      <p className="text-2xl font-bold text-blue-600">{emailReminderResults.total_users_with_due_actions}</p>
                      <p className="text-sm text-gray-600">Users with due actions</p>
                    </div>
                    <div className="p-3 bg-white rounded">
                      <p className="text-2xl font-bold text-orange-600">{emailReminderResults.total_actions_due}</p>
                      <p className="text-sm text-gray-600">Total actions due</p>
                    </div>
                  </div>

                  {emailReminderResults.emails && emailReminderResults.emails.length > 0 && (
                    <div className="space-y-2">
                      <h5 className="font-medium text-gray-700">Email Recipients:</h5>
                      <div className="max-h-60 overflow-y-auto space-y-2">
                        {emailReminderResults.emails.map((email, idx) => (
                          <div key={idx} className="flex items-center justify-between p-2 bg-white rounded border">
                            <div>
                              <p className="font-medium">{email.name}</p>
                              <p className="text-sm text-gray-500">{email.email}</p>
                            </div>
                            <div className="text-right">
                              <span className={`px-2 py-1 rounded text-xs ${
                                email.status === 'sent' ? 'bg-green-100 text-green-700' :
                                email.status === 'would_send' ? 'bg-blue-100 text-blue-700' :
                                'bg-red-100 text-red-700'
                              }`}>
                                {email.status === 'would_send' ? 'Would Send' : email.status}
                              </span>
                              <p className="text-sm text-gray-600 mt-1">
                                {email.action_count} action(s)
                                {email.overdue > 0 && <span className="text-red-600 ml-1">({email.overdue} overdue)</span>}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {emailReminderResults.emails?.length === 0 && (
                    <p className="text-gray-500 text-center py-4">No users have actions due within 7 days.</p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* SMTP Configuration Status */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-green-700">
                <CheckCircle className="w-5 h-5" />
                Email Configuration Active
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600 mb-3">
                SMTP is configured and ready to send emails. The system uses:
              </p>
              <div className="bg-green-50 p-3 rounded text-sm">
                <p><strong>From:</strong> hello@holboroughlakes.uk</p>
                <p><strong>Provider:</strong> Google Workspace (Gmail)</p>
              </div>
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded">
                <p className="text-sm text-blue-800">
                  <strong>Tip:</strong> Always use &quot;Preview (Dry Run)&quot; first to see who will receive emails 
                  before sending actual reminders.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Automated Scheduler */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-purple-600" />
                Automated Weekly Reminders
              </CardTitle>
              <CardDescription>
                Configure automatic email reminders to be sent on a weekly schedule.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Enable/Disable Toggle */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium">Enable Automated Reminders</p>
                  <p className="text-sm text-gray-500">
                    {schedulerSettings?.enabled 
                      ? 'Reminders will be sent automatically each week' 
                      : 'Reminders are currently manual only'}
                  </p>
                </div>
                <Switch
                  checked={schedulerSettings?.enabled || false}
                  onCheckedChange={(checked) => updateSchedulerSettings({ enabled: checked })}
                  disabled={schedulerLoading}
                />
              </div>

              {/* Schedule Configuration */}
              {schedulerSettings?.enabled && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 border rounded-lg">
                  <div className="space-y-2">
                    <Label>Day of Week</Label>
                    <Select
                      value={schedulerSettings?.day_of_week || 'mon'}
                      onValueChange={(value) => updateSchedulerSettings({ day_of_week: value })}
                      disabled={schedulerLoading}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="mon">Monday</SelectItem>
                        <SelectItem value="tue">Tuesday</SelectItem>
                        <SelectItem value="wed">Wednesday</SelectItem>
                        <SelectItem value="thu">Thursday</SelectItem>
                        <SelectItem value="fri">Friday</SelectItem>
                        <SelectItem value="sat">Saturday</SelectItem>
                        <SelectItem value="sun">Sunday</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Hour (24h format)</Label>
                    <Select
                      value={String(schedulerSettings?.hour || 8)}
                      onValueChange={(value) => updateSchedulerSettings({ hour: parseInt(value) })}
                      disabled={schedulerLoading}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 24 }, (_, i) => (
                          <SelectItem key={i} value={String(i)}>
                            {String(i).padStart(2, '0')}:00
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Timezone</Label>
                    <Input 
                      value={schedulerSettings?.timezone || 'Europe/London'} 
                      disabled 
                      className="bg-gray-100"
                    />
                  </div>
                </div>
              )}

              {/* Next Run Info */}
              {schedulerSettings?.enabled && schedulerSettings?.next_run && (
                <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                  <div className="flex items-center gap-2 text-purple-700">
                    <Clock className="w-5 h-5" />
                    <span className="font-medium">Next Scheduled Run:</span>
                    <span>
                      {new Date(schedulerSettings.next_run).toLocaleString('en-GB', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>
                </div>
              )}

              {/* Manual Trigger */}
              <div className="flex items-center gap-4">
                <Button
                  variant="outline"
                  onClick={runSchedulerNow}
                  disabled={schedulerLoading}
                  className="flex items-center gap-2"
                >
                  {schedulerLoading ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Play className="w-4 h-4" />
                  )}
                  Run Now (Send Immediately)
                </Button>
                <span className="text-sm text-gray-500">
                  Manually trigger reminders without waiting for schedule
                </span>
              </div>

              {/* Run History */}
              {schedulerHistory.length > 0 && (
                <div className="space-y-3">
                  <h4 className="font-medium text-gray-700 flex items-center gap-2">
                    <History className="w-4 h-4" />
                    Recent Automated Runs
                  </h4>
                  <div className="max-h-48 overflow-y-auto space-y-2">
                    {schedulerHistory.map((run) => (
                      <div 
                        key={run.id} 
                        className={`p-3 rounded-lg border text-sm ${
                          run.status === 'completed' ? 'bg-green-50 border-green-200' :
                          run.status === 'failed' ? 'bg-red-50 border-red-200' :
                          'bg-gray-50 border-gray-200'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {run.status === 'completed' ? (
                              <CheckCircle className="w-4 h-4 text-green-600" />
                            ) : run.status === 'failed' ? (
                              <AlertCircle className="w-4 h-4 text-red-600" />
                            ) : (
                              <RefreshCw className="w-4 h-4 text-gray-600 animate-spin" />
                            )}
                            <span className="font-medium">
                              {new Date(run.started_at).toLocaleString('en-GB', {
                                day: '2-digit',
                                month: 'short',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                          </div>
                          <div className="text-right">
                            {run.status === 'completed' && (
                              <span className="text-green-700">
                                {run.users_emailed} user(s), {run.actions_included} action(s)
                              </span>
                            )}
                            {run.status === 'failed' && (
                              <span className="text-red-700">Failed</span>
                            )}
                          </div>
                        </div>
                        {run.error_message && (
                          <p className="text-red-600 text-xs mt-1">{run.error_message}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Weekly Stats Digest Card */}
          <Card data-testid="weekly-digest-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-indigo-600" />
                Weekly Stats Digest
              </CardTitle>
              <CardDescription>
                Automated weekly email summarizing key metrics: new actions, closed actions, overdue items, and more.
                Sent every Friday to selected user roles.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Current Status Banner */}
              {digestSettings?.enabled ? (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="font-semibold text-green-800">Weekly Digest is ACTIVE</p>
                      <p className="text-sm text-green-700 mt-1">
                        Sending every <strong>
                          {digestSettings?.day_of_week === 'mon' ? 'Monday' :
                           digestSettings?.day_of_week === 'tue' ? 'Tuesday' :
                           digestSettings?.day_of_week === 'wed' ? 'Wednesday' :
                           digestSettings?.day_of_week === 'thu' ? 'Thursday' :
                           digestSettings?.day_of_week === 'fri' ? 'Friday' :
                           digestSettings?.day_of_week === 'sat' ? 'Saturday' : 'Sunday'}
                        </strong> at <strong>{String(digestSettings?.hour || 9).padStart(2, '0')}:00</strong> to: {' '}
                        <span className="font-medium">
                          {(() => {
                            const roles = digestSettings?.recipient_roles || [];
                            const roleLabels = [];
                            if (roles.includes('admin') || roles.includes('super_admin')) roleLabels.push('Admins');
                            if (roles.includes('committee')) roleLabels.push('Group Members');
                            if (roles.includes('external')) roleLabels.push('External Partners');
                            return roleLabels.length > 0 ? roleLabels.join(', ') : 'No recipients selected';
                          })()}
                        </span>
                      </p>
                      {digestSettings?.next_run && (
                        <p className="text-xs text-green-600 mt-1">
                          Next send: {new Date(digestSettings.next_run).toLocaleString('en-GB', {
                            weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
                          })}
                        </p>
                      )}
                    </div>
                    <Switch
                      checked={true}
                      onCheckedChange={(checked) => updateDigestSettings({ enabled: checked })}
                      disabled={digestLoading}
                      data-testid="digest-enable-switch"
                      className="data-[state=checked]:bg-green-600 data-[state=unchecked]:bg-gray-300"
                    />
                  </div>
                </div>
              ) : (
                <div className="p-4 bg-gray-100 border border-gray-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <AlertCircle className="w-6 h-6 text-gray-400 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="font-semibold text-gray-600">Weekly Digest is DISABLED</p>
                      <p className="text-sm text-gray-500">Enable to send automated weekly summary emails</p>
                    </div>
                    <Switch
                      checked={false}
                      onCheckedChange={(checked) => updateDigestSettings({ enabled: checked })}
                      disabled={digestLoading}
                      data-testid="digest-enable-switch"
                      className="data-[state=checked]:bg-green-600 data-[state=unchecked]:bg-gray-300"
                    />
                  </div>
                </div>
              )}

              {/* Recipient Roles Configuration */}
              <div className="p-4 border rounded-lg space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-gray-700">Recipient Roles</h4>
                    <p className="text-sm text-gray-500">Select which user roles should receive the weekly digest:</p>
                  </div>
                  {/* Show count of selected roles */}
                  <div className="text-sm">
                    <span className={`px-2 py-1 rounded-full ${
                      (digestSettings?.recipient_roles?.length || 0) > 0 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-red-100 text-red-700'
                    }`}>
                      {(() => {
                        const roles = digestSettings?.recipient_roles || [];
                        let count = 0;
                        if (roles.includes('admin') || roles.includes('super_admin')) count++;
                        if (roles.includes('committee')) count++;
                        if (roles.includes('external')) count++;
                        return `${count} role${count !== 1 ? 's' : ''} selected`;
                      })()}
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className={`flex items-center justify-between p-3 rounded-lg border-2 transition-colors ${
                    digestSettings?.recipient_roles?.includes('admin') || digestSettings?.recipient_roles?.includes('super_admin')
                      ? 'bg-blue-50 border-blue-300'
                      : 'bg-gray-50 border-gray-200'
                  }`}>
                    <div className="flex items-center gap-2">
                      <Shield className={`w-4 h-4 ${
                        digestSettings?.recipient_roles?.includes('admin') || digestSettings?.recipient_roles?.includes('super_admin')
                          ? 'text-blue-600'
                          : 'text-gray-400'
                      }`} />
                      <span className={`text-sm font-medium ${
                        digestSettings?.recipient_roles?.includes('admin') || digestSettings?.recipient_roles?.includes('super_admin')
                          ? 'text-blue-700'
                          : 'text-gray-600'
                      }`}>Admins</span>
                    </div>
                    <Switch
                      checked={digestSettings?.recipient_roles?.includes('admin') || digestSettings?.recipient_roles?.includes('super_admin') || false}
                      onCheckedChange={(checked) => {
                        const currentRoles = digestSettings?.recipient_roles || [];
                        let newRoles;
                        if (checked) {
                          newRoles = [...new Set([...currentRoles, 'admin', 'super_admin'])];
                        } else {
                          newRoles = currentRoles.filter(r => r !== 'admin' && r !== 'super_admin');
                        }
                        updateDigestSettings({ recipient_roles: newRoles });
                      }}
                      disabled={digestLoading}
                      data-testid="digest-role-admin"
                      className="data-[state=checked]:bg-blue-600 data-[state=unchecked]:bg-gray-300"
                    />
                  </div>
                  <div className={`flex items-center justify-between p-3 rounded-lg border-2 transition-colors ${
                    digestSettings?.recipient_roles?.includes('committee')
                      ? 'bg-green-50 border-green-300'
                      : 'bg-gray-50 border-gray-200'
                  }`}>
                    <div className="flex items-center gap-2">
                      <Activity className={`w-4 h-4 ${
                        digestSettings?.recipient_roles?.includes('committee')
                          ? 'text-green-600'
                          : 'text-gray-400'
                      }`} />
                      <span className={`text-sm font-medium ${
                        digestSettings?.recipient_roles?.includes('committee')
                          ? 'text-green-700'
                          : 'text-gray-600'
                      }`}>Group Members</span>
                    </div>
                    <Switch
                      checked={digestSettings?.recipient_roles?.includes('committee') || false}
                      onCheckedChange={(checked) => {
                        const currentRoles = digestSettings?.recipient_roles || [];
                        let newRoles;
                        if (checked) {
                          newRoles = [...new Set([...currentRoles, 'committee'])];
                        } else {
                          newRoles = currentRoles.filter(r => r !== 'committee');
                        }
                        updateDigestSettings({ recipient_roles: newRoles });
                      }}
                      disabled={digestLoading}
                      data-testid="digest-role-committee"
                      className="data-[state=checked]:bg-green-600 data-[state=unchecked]:bg-gray-300"
                    />
                  </div>
                  <div className={`flex items-center justify-between p-3 rounded-lg border-2 transition-colors ${
                    digestSettings?.recipient_roles?.includes('external')
                      ? 'bg-orange-50 border-orange-300'
                      : 'bg-gray-50 border-gray-200'
                  }`}>
                    <div className="flex items-center gap-2">
                      <Mail className={`w-4 h-4 ${
                        digestSettings?.recipient_roles?.includes('external')
                          ? 'text-orange-600'
                          : 'text-gray-400'
                      }`} />
                      <span className={`text-sm font-medium ${
                        digestSettings?.recipient_roles?.includes('external')
                          ? 'text-orange-700'
                          : 'text-gray-600'
                      }`}>External Partners</span>
                    </div>
                    <Switch
                      checked={digestSettings?.recipient_roles?.includes('external') || false}
                      onCheckedChange={(checked) => {
                        const currentRoles = digestSettings?.recipient_roles || [];
                        let newRoles;
                        if (checked) {
                          newRoles = [...new Set([...currentRoles, 'external'])];
                        } else {
                          newRoles = currentRoles.filter(r => r !== 'external');
                        }
                        updateDigestSettings({ recipient_roles: newRoles });
                      }}
                      disabled={digestLoading}
                      data-testid="digest-role-external"
                      className="data-[state=checked]:bg-orange-600 data-[state=unchecked]:bg-gray-300"
                    />
                  </div>
                </div>
              </div>

              {/* Schedule Configuration */}
              {digestSettings?.enabled && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 border rounded-lg">
                  <div className="space-y-2">
                    <Label>Day of Week</Label>
                    <Select
                      value={digestSettings?.day_of_week || 'fri'}
                      onValueChange={(value) => updateDigestSettings({ day_of_week: value })}
                      disabled={digestLoading}
                    >
                      <SelectTrigger data-testid="digest-day-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="mon">Monday</SelectItem>
                        <SelectItem value="tue">Tuesday</SelectItem>
                        <SelectItem value="wed">Wednesday</SelectItem>
                        <SelectItem value="thu">Thursday</SelectItem>
                        <SelectItem value="fri">Friday</SelectItem>
                        <SelectItem value="sat">Saturday</SelectItem>
                        <SelectItem value="sun">Sunday</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Hour (24h format)</Label>
                    <Select
                      value={String(digestSettings?.hour || 9)}
                      onValueChange={(value) => updateDigestSettings({ hour: parseInt(value) })}
                      disabled={digestLoading}
                    >
                      <SelectTrigger data-testid="digest-hour-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 24 }, (_, i) => (
                          <SelectItem key={i} value={String(i)}>
                            {String(i).padStart(2, '0')}:00
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Timezone</Label>
                    <Input 
                      value={digestSettings?.timezone || 'Europe/London'} 
                      disabled 
                      className="bg-gray-100"
                    />
                  </div>
                </div>
              )}

              {/* Next Run Info */}
              {digestSettings?.enabled && digestSettings?.next_run && (
                <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
                  <div className="flex items-center gap-2 text-indigo-700">
                    <Clock className="w-5 h-5" />
                    <span className="font-medium">Next Scheduled Digest:</span>
                    <span>
                      {new Date(digestSettings.next_run).toLocaleString('en-GB', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>
                </div>
              )}

              {/* What's Included Info */}
              <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
                <h4 className="font-medium text-indigo-800 mb-2">What&apos;s included in the digest:</h4>
                <ul className="text-sm text-indigo-700 space-y-1">
                  <li>• <strong>New actions</strong> created this week</li>
                  <li>• <strong>Closed actions</strong> completed this week</li>
                  <li>• <strong>Overdue actions</strong> requiring attention</li>
                  <li>• <strong>Due this week</strong> upcoming deadlines</li>
                  <li>• Personal action summary for each recipient</li>
                </ul>
              </div>

              {/* Manual Actions */}
              <div className="flex items-center gap-4">
                <Button
                  variant="outline"
                  onClick={previewDigest}
                  disabled={digestLoading}
                  className="flex items-center gap-2"
                  data-testid="digest-preview-btn"
                >
                  {digestLoading ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Search className="w-4 h-4" />
                  )}
                  Preview Digest
                </Button>
                <Button
                  onClick={sendDigestNow}
                  disabled={digestLoading || !digestSettings?.recipient_roles?.length}
                  className="bg-indigo-600 hover:bg-indigo-700 flex items-center gap-2"
                  data-testid="digest-send-now-btn"
                >
                  {digestLoading ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  Send Now
                </Button>
                <span className="text-sm text-gray-500">
                  Manually send digest without waiting for schedule
                </span>
              </div>

              {/* Digest Preview Dialog */}
              <Dialog open={showDigestPreview} onOpenChange={setShowDigestPreview}>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Weekly Digest Preview</DialogTitle>
                    <DialogDescription>
                      Preview of the weekly digest email for {digestPreview?.preview_for || 'recipients'}. Period: {digestPreview?.period_start} to {digestPreview?.period_end}
                    </DialogDescription>
                  </DialogHeader>
                  {digestPreview && (
                    <div className="space-y-4">
                      {/* Quick Stats Summary */}
                      <div className="grid grid-cols-5 gap-3">
                        <div className="p-2 bg-green-50 rounded-lg text-center">
                          <p className="text-xl font-bold text-green-600">{digestPreview.stats?.new_this_week || 0}</p>
                          <p className="text-xs text-gray-600">New</p>
                        </div>
                        <div className="p-2 bg-blue-50 rounded-lg text-center">
                          <p className="text-xl font-bold text-blue-600">{digestPreview.stats?.closed_this_week || 0}</p>
                          <p className="text-xs text-gray-600">Closed</p>
                        </div>
                        <div className="p-2 bg-red-50 rounded-lg text-center">
                          <p className="text-xl font-bold text-red-600">{digestPreview.stats?.overdue || 0}</p>
                          <p className="text-xs text-gray-600">Overdue</p>
                        </div>
                        <div className="p-2 bg-orange-50 rounded-lg text-center">
                          <p className="text-xl font-bold text-orange-600">{digestPreview.stats?.due_this_week || 0}</p>
                          <p className="text-xs text-gray-600">Due Soon</p>
                        </div>
                        <div className="p-2 bg-gray-50 rounded-lg text-center">
                          <p className="text-xl font-bold text-gray-600">{digestPreview.stats?.total_active || 0}</p>
                          <p className="text-xs text-gray-600">Active</p>
                        </div>
                      </div>
                      
                      {/* Email Preview */}
                      <div className="border rounded-lg overflow-hidden">
                        <div className="bg-gray-100 px-3 py-2 border-b flex items-center gap-2">
                          <Mail className="w-4 h-4 text-gray-500" />
                          <span className="text-sm font-medium text-gray-700">Email Preview</span>
                        </div>
                        <div 
                          className="bg-white p-0"
                          style={{ maxHeight: '500px', overflowY: 'auto' }}
                        >
                          <iframe
                            srcDoc={digestPreview.html}
                            title="Digest Email Preview"
                            className="w-full border-0"
                            style={{ minHeight: '500px', height: '100%' }}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowDigestPreview(false)}>
                      Close
                    </Button>
                    <Button 
                      onClick={sendDigestNow}
                      disabled={digestLoading || !digestSettings?.recipient_roles?.length}
                      className="bg-indigo-600 hover:bg-indigo-700"
                    >
                      {digestLoading ? 'Sending...' : 'Send Digest Now'}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              {/* Digest Run History */}
              {digestHistory.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-gray-700 flex items-center gap-2">
                      <History className="w-4 h-4" />
                      Recent Digest Runs
                      <span className="text-xs text-gray-400">({digestHistory.length} records, auto-clears after 14 days)</span>
                    </h4>
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          disabled={digestLoading}
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Clear History
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Clear Digest History</DialogTitle>
                          <DialogDescription>
                            This will permanently delete all digest run history records. This action cannot be undone.
                          </DialogDescription>
                        </DialogHeader>
                        <DialogFooter>
                          <DialogClose asChild>
                            <Button variant="outline">Cancel</Button>
                          </DialogClose>
                          <DialogClose asChild>
                            <Button 
                              variant="destructive"
                              onClick={clearDigestHistory}
                            >
                              Clear All History
                            </Button>
                          </DialogClose>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </div>
                  <div className="max-h-48 overflow-y-auto space-y-2">
                    {digestHistory.map((run) => (
                      <div 
                        key={run.id} 
                        className={`p-3 rounded-lg border text-sm ${
                          run.status === 'completed' ? 'bg-green-50 border-green-200' :
                          run.status === 'failed' ? 'bg-red-50 border-red-200' :
                          'bg-gray-50 border-gray-200'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {run.status === 'completed' ? (
                              <CheckCircle className="w-4 h-4 text-green-600" />
                            ) : run.status === 'failed' ? (
                              <AlertCircle className="w-4 h-4 text-red-600" />
                            ) : (
                              <RefreshCw className="w-4 h-4 text-gray-600 animate-spin" />
                            )}
                            <span className="font-medium">
                              {new Date(run.started_at).toLocaleString('en-GB', {
                                day: '2-digit',
                                month: 'short',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                            <span className={`px-2 py-0.5 rounded text-xs ${
                              run.trigger === 'manual' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                            }`}>
                              {run.trigger === 'manual' ? 'Manual' : 'Scheduled'}
                            </span>
                          </div>
                          <div className="text-right">
                            {run.status === 'completed' && (
                              <span className="text-green-700">
                                {run.emails_sent} email(s) sent
                              </span>
                            )}
                            {run.status === 'failed' && (
                              <span className="text-red-700">Failed</span>
                            )}
                          </div>
                        </div>
                        {run.error_message && (
                          <p className="text-red-600 text-xs mt-1">{run.error_message}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Archived Actions Tab */}
        <TabsContent value="archived" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Archived Actions ({archivedActions.length})</CardTitle>
              <CardDescription>
                Actions that have been soft-deleted. You can restore them or permanently delete them.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {archivedActions.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No archived actions</p>
              ) : (
                <div className="space-y-3">
                  {archivedActions.map((action) => (
                    <div 
                      key={action.id} 
                      className="flex items-center justify-between p-4 border rounded-lg bg-gray-50"
                      data-testid={`archived-action-${action.id}`}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-sm text-gray-500">{action.reference}</span>
                          <span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded text-xs">Archived</span>
                        </div>
                        <p className="font-medium mt-1">{action.title}</p>
                        <p className="text-sm text-gray-500">
                          Archived: {action.archived_at ? formatDate(action.archived_at) : 'Unknown'}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleRestoreAction(action.id)}
                          data-testid={`restore-action-${action.id}`}
                        >
                          <RotateCcw className="w-4 h-4 mr-1" />
                          Restore
                        </Button>
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button 
                              variant="destructive" 
                              size="sm"
                              onClick={() => setActionToDelete(action)}
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Delete
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle className="text-red-600">Permanently Delete Action</DialogTitle>
                              <DialogDescription>
                                Are you sure you want to permanently delete action <strong>{action.reference}</strong>?
                                This action cannot be undone.
                              </DialogDescription>
                            </DialogHeader>
                            <DialogFooter>
                              <Button variant="outline" onClick={() => setActionToDelete(null)}>
                                Cancel
                              </Button>
                              <Button 
                                variant="destructive" 
                                onClick={handlePermanentDelete}
                                data-testid="confirm-delete-button"
                              >
                                Delete Permanently
                              </Button>
                            </DialogFooter>
                          </DialogContent>
                        </Dialog>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Reference Integrity Tab */}
        <TabsContent value="integrity" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {referenceCheck?.integrity_status === 'OK' ? (
                  <CheckCircle className="w-5 h-5 text-green-500" />
                ) : (
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                )}
                Reference Integrity Status
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600">Total Actions</p>
                  <p className="text-2xl font-bold">{referenceCheck?.total_actions || 0}</p>
                </div>
                <div className="p-4 bg-green-50 rounded-lg">
                  <p className="text-sm text-green-600">Active</p>
                  <p className="text-2xl font-bold text-green-700">{referenceCheck?.active_actions || 0}</p>
                </div>
                <div className="p-4 bg-orange-50 rounded-lg">
                  <p className="text-sm text-orange-600">Archived</p>
                  <p className="text-2xl font-bold text-orange-700">{referenceCheck?.archived_actions || 0}</p>
                </div>
                <div className="p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-600">Next Reference</p>
                  <p className="text-2xl font-bold text-blue-700">{referenceCheck?.next_reference || 'ACT-0001'}</p>
                </div>
              </div>

              {referenceCheck?.duplicates?.length > 0 && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg space-y-3">
                  <p className="font-medium text-red-700">⚠️ Duplicate References Found</p>
                  <p className="text-sm text-red-600">{referenceCheck.duplicates.join(', ')}</p>
                  
                  {/* Detailed duplicate records */}
                  {referenceCheck.duplicate_details?.length > 0 && (
                    <div className="mt-3 border-t border-red-200 pt-3">
                      <p className="text-sm font-medium text-red-700 mb-2">Duplicate Record Details:</p>
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {referenceCheck.duplicate_details.map((dup, idx) => (
                          <div key={idx} className="text-xs bg-white p-2 rounded border border-red-100 flex justify-between items-start">
                            <div>
                              <span className="font-mono font-bold text-red-600">{dup.reference}</span>
                              <span className="mx-2">-</span>
                              <span className="text-gray-700">{dup.title}</span>
                              <div className="text-gray-500 mt-1">
                                Group: <span className={dup.group_name === 'No Group' ? 'text-red-500 font-medium' : ''}>{dup.group_name}</span>
                                {dup.is_linked && <span className="ml-2 px-1 bg-blue-100 text-blue-700 rounded">Linked Action</span>}
                                {dup.is_archived && <span className="ml-2 px-1 bg-gray-100 text-gray-700 rounded">Archived</span>}
                              </div>
                              <div className="text-gray-400">Created: {new Date(dup.created_at).toLocaleString()}</div>
                            </div>
                            <code className="text-xs text-gray-400 bg-gray-50 px-1 rounded">{dup.id?.slice(0, 8)}...</code>
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-red-500 mt-2">
                        💡 Tip: Records with &quot;No Group&quot; or &quot;Linked Action&quot; badge are likely the duplicates created by the bug. 
                        You can delete them from the Actions page or use Renumber below.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {referenceCheck?.total_gaps > 0 && (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="font-medium text-yellow-700 mb-2">ℹ️ Gaps in Reference Sequence</p>
                  <p className="text-sm text-yellow-600">
                    {referenceCheck.total_gaps} gap(s) found. This is normal when actions are archived.
                  </p>
                  {referenceCheck.gaps?.length > 0 && (
                    <p className="text-xs text-yellow-500 mt-1">
                      Missing: ACT-{referenceCheck.gaps.slice(0, 10).map(n => n.toString().padStart(4, '0')).join(', ACT-')}
                      {referenceCheck.gaps.length > 10 && '...'}
                    </p>
                  )}
                </div>
              )}

              {referenceCheck?.integrity_status === 'OK' && referenceCheck?.total_gaps === 0 && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="font-medium text-green-700">✓ All references are unique and sequential</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Renumber Actions Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Hash className="w-5 h-5 text-purple-600" />
                Renumber Actions
              </CardTitle>
              <CardDescription>
                Re-sequence action references within each group using their respective prefixes 
                (e.g., ACT-0001 for HLRA, TRA-0001 for HL Transition, SOC-0001 for Social).
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-3">
                <Button 
                  variant="outline"
                  onClick={() => runRenumber(true)}
                  disabled={renumberLoading}
                >
                  {renumberLoading ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Search className="w-4 h-4 mr-2" />
                  )}
                  Preview Changes
                </Button>
                <Dialog open={showRenumberDialog} onOpenChange={setShowRenumberDialog}>
                  <DialogTrigger asChild>
                    <Button 
                      className="bg-purple-600 hover:bg-purple-700"
                      disabled={renumberLoading}
                    >
                      <Hash className="w-4 h-4 mr-2" />
                      Renumber All Actions
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle className="text-purple-700">⚠️ Renumber All Actions</DialogTitle>
                      <DialogDescription>
                        This will reassign reference numbers within each group using their configured prefix:
                        <br /><br />
                        • HLRA actions → ACT-0001, ACT-0002, etc.
                        <br />
                        • HL Transition actions → TRA-0001, TRA-0002, etc.
                        <br />
                        • Social actions → SOC-0001, SOC-0002, etc.
                        <br /><br />
                        <strong>Note:</strong> Existing references in reports, emails, or external 
                        documents will become invalid.
                      </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setShowRenumberDialog(false)}>Cancel</Button>
                      <Button 
                        className="bg-purple-600 hover:bg-purple-700"
                        onClick={() => runRenumber(false)}
                        disabled={renumberLoading}
                      >
                        {renumberLoading ? 'Processing...' : 'Confirm Renumber'}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>

              {/* Renumber Results */}
              {renumberResults && (
                <div className={`p-4 rounded-lg border ${renumberResults.dry_run ? 'bg-blue-50 border-blue-200' : 'bg-green-50 border-green-200'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    {renumberResults.dry_run ? (
                      <Search className="w-5 h-5 text-blue-600" />
                    ) : (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    )}
                    <span className={`font-semibold ${renumberResults.dry_run ? 'text-blue-700' : 'text-green-700'}`}>
                      {renumberResults.dry_run ? 'Preview Results' : 'Renumber Complete'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700 mb-1">
                    <strong>Total Actions:</strong> {renumberResults.total_actions}
                  </p>
                  <p className="text-sm text-gray-700 mb-3">
                    <strong>Changes Needed:</strong> {renumberResults.changes_needed}
                  </p>
                  
                  {/* Groups Summary */}
                  {renumberResults.groups_processed && renumberResults.groups_processed.length > 0 && (
                    <div className="mb-3 space-y-2">
                      <p className="text-sm font-medium text-gray-700">By Group:</p>
                      {renumberResults.groups_processed.map((group, i) => (
                        <div key={i} className="text-xs bg-white p-2 rounded flex justify-between items-center">
                          <span className="font-medium">{group.group_name}</span>
                          <span className="text-gray-500">
                            {group.total_actions} actions ({group.prefix}-NNNN) • {group.changes_needed} changes
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {renumberResults.changes && renumberResults.changes.length > 0 && (
                    <div className="text-xs bg-white p-2 rounded max-h-48 overflow-y-auto">
                      <p className="text-sm font-medium text-gray-700 mb-2">Change Details:</p>
                      {renumberResults.changes.slice(0, 20).map((change, i) => (
                        <div key={i} className="mb-1 font-mono flex items-center gap-2">
                          <span className="text-red-500 line-through">{change.old_reference}</span>
                          <span>→</span>
                          <span className="text-green-600">{change.new_reference}</span>
                          <span className="text-gray-400 text-xs truncate flex-1">({change.group_name}) {change.title}</span>
                        </div>
                      ))}
                      {renumberResults.changes.length > 20 && (
                        <p className="text-gray-400 mt-2">...and {renumberResults.changes.length - 20} more changes</p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
          {/* Orphan Actions Cleanup Card */}
          <Card className="border-orange-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-orange-600" />
                Orphan Actions Cleanup
              </CardTitle>
              <CardDescription>
                Actions with no valid group assignment (caused by the linked action bug). 
                These actions don&apos;t appear in normal group views but are counted in totals.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-3 flex-wrap">
                <Button 
                  variant="outline"
                  onClick={async () => {
                    try {
                      const res = await axios.post(`${API}/admin/cleanup-orphans`, 
                        { cleanup_type: 'list_orphan_actions', dry_run: true }
                      );
                      setOrphanResults(res.data);
                    } catch (err) {
                      toast.error('Failed to list orphan actions');
                    }
                  }}
                >
                  <Search className="w-4 h-4 mr-2" />
                  Find Orphan Actions
                </Button>
                
                {orphanResults?.count > 0 && (
                  <>
                    <Button 
                      variant="outline"
                      className="text-red-600 border-red-300 hover:bg-red-50"
                      onClick={async () => {
                        if (!confirm(`Are you sure you want to DELETE ${orphanResults.count} orphan actions? This cannot be undone.`)) return;
                        try {
                          const res = await axios.post(`${API}/admin/cleanup-orphans`, 
                            { cleanup_type: 'delete_orphan_actions', dry_run: false }
                          );
                          toast.success(`Deleted ${res.data.count} orphan actions`);
                          setOrphanResults(null);
                          // Refresh stats
                          fetchData();
                        } catch (err) {
                          toast.error('Failed to delete orphan actions');
                        }
                      }}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete All Orphans
                    </Button>
                  </>
                )}
              </div>

              {/* Orphan Results */}
              {orphanResults && (
                <div className={`p-4 rounded-lg border ${orphanResults.count > 0 ? 'bg-orange-50 border-orange-200' : 'bg-green-50 border-green-200'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    {orphanResults.count > 0 ? (
                      <AlertTriangle className="w-5 h-5 text-orange-600" />
                    ) : (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    )}
                    <span className={`font-semibold ${orphanResults.count > 0 ? 'text-orange-700' : 'text-green-700'}`}>
                      {orphanResults.count > 0 ? `${orphanResults.count} Orphan Actions Found` : 'No Orphan Actions'}
                    </span>
                  </div>
                  
                  {orphanResults.count > 0 && orphanResults.affected_records && (
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {orphanResults.affected_records.map((rec, idx) => (
                        <div key={idx} className="text-xs bg-white p-2 rounded border border-orange-100 flex justify-between items-start">
                          <div>
                            <span className="font-mono font-bold text-orange-600">{rec.reference}</span>
                            <span className="mx-2">-</span>
                            <span className="text-gray-700">{rec.title}</span>
                            {rec.is_linked && <span className="ml-2 px-1 bg-blue-100 text-blue-700 rounded text-xs">Linked Action</span>}
                            <div className="text-gray-400 mt-1">Created: {rec.created_at ? new Date(rec.created_at).toLocaleString() : 'Unknown'}</div>
                          </div>
                          <code className="text-xs text-gray-400 bg-gray-50 px-1 rounded">{rec.id?.slice(0, 8)}...</code>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {orphanResults.count === 0 && (
                    <p className="text-sm text-green-600">All actions have valid group assignments.</p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Date Normalization Card */}
          <Card className="border-blue-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-blue-600" />
                Date Format Normalization
              </CardTitle>
              <CardDescription>
                Convert all dates in the database to ISO format (YYYY-MM-DD). 
                This fixes issues with dates stored in DD/MM/YYYY format that cause incorrect overdue calculations.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-3 flex-wrap">
                <Button 
                  variant="outline"
                  onClick={async () => {
                    try {
                      const res = await axios.post(`${API}/admin/normalize-dates`, 
                        { dry_run: true }
                      );
                      setDateNormResults(res.data);
                    } catch (err) {
                      toast.error('Failed to scan dates');
                    }
                  }}
                >
                  <Search className="w-4 h-4 mr-2" />
                  Preview Changes
                </Button>
                
                {dateNormResults?.actions_updated > 0 && (
                  <Button 
                    className="bg-blue-600 hover:bg-blue-700"
                    onClick={async () => {
                      if (!confirm(`Are you sure you want to normalize ${dateNormResults.fields_normalized} date fields? This will update ${dateNormResults.actions_updated} actions.`)) return;
                      try {
                        const res = await axios.post(`${API}/admin/normalize-dates`, 
                          { dry_run: false }
                        );
                        toast.success(`Normalized ${res.data.fields_normalized} date fields`);
                        setDateNormResults(res.data);
                      } catch (err) {
                        toast.error('Failed to normalize dates');
                      }
                    }}
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Apply Normalization
                  </Button>
                )}
              </div>

              {/* Date Normalization Results */}
              {dateNormResults && (
                <div className={`p-4 rounded-lg border ${dateNormResults.actions_updated > 0 ? 'bg-blue-50 border-blue-200' : 'bg-green-50 border-green-200'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    {dateNormResults.actions_updated > 0 ? (
                      <Calendar className="w-5 h-5 text-blue-600" />
                    ) : (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    )}
                    <span className={`font-semibold ${dateNormResults.actions_updated > 0 ? 'text-blue-700' : 'text-green-700'}`}>
                      {dateNormResults.dry_run ? 'Preview Results' : 'Normalization Complete'}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4 mb-3">
                    <div className="text-center p-2 bg-white rounded">
                      <p className="text-xl font-bold text-gray-700">{dateNormResults.actions_scanned}</p>
                      <p className="text-xs text-gray-500">Actions Scanned</p>
                    </div>
                    <div className="text-center p-2 bg-white rounded">
                      <p className={`text-xl font-bold ${dateNormResults.actions_updated > 0 ? 'text-blue-600' : 'text-green-600'}`}>{dateNormResults.actions_updated}</p>
                      <p className="text-xs text-gray-500">Actions to Update</p>
                    </div>
                    <div className="text-center p-2 bg-white rounded">
                      <p className={`text-xl font-bold ${dateNormResults.fields_normalized > 0 ? 'text-blue-600' : 'text-green-600'}`}>{dateNormResults.fields_normalized}</p>
                      <p className="text-xs text-gray-500">Fields to Normalize</p>
                    </div>
                  </div>
                  
                  {dateNormResults.actions_updated > 0 && dateNormResults.changes && (
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      <p className="text-sm font-medium text-gray-700">Sample Changes:</p>
                      {dateNormResults.changes.slice(0, 20).map((rec, idx) => (
                        <div key={idx} className="text-xs bg-white p-2 rounded border border-blue-100">
                          <span className="font-mono font-bold text-blue-600">{rec.reference}</span>
                          {rec.changes.map((change, i) => (
                            <div key={i} className="ml-4 mt-1">
                              <span className="text-gray-500">{change.field}:</span>
                              <span className="text-red-500 line-through ml-2">{change.original}</span>
                              <span className="mx-1">→</span>
                              <span className="text-green-600">{change.normalized}</span>
                            </div>
                          ))}
                        </div>
                      ))}
                      {dateNormResults.changes.length > 20 && (
                        <p className="text-xs text-gray-400">...and {dateNormResults.changes.length - 20} more actions</p>
                      )}
                    </div>
                  )}
                  
                  {dateNormResults.actions_updated === 0 && (
                    <p className="text-sm text-green-600">All dates are already in ISO format (YYYY-MM-DD). No changes needed.</p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Audit Log Tab */}
        <TabsContent value="audit" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Audit Log Management</CardTitle>
                  <CardDescription>
                    Export, archive, and manage audit log entries
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={exportAuditLog}>
                    <Download className="w-4 h-4 mr-2" />
                    Export to Excel
                  </Button>
                  <Dialog open={showAuditArchiveDialog} onOpenChange={setShowAuditArchiveDialog}>
                    <DialogTrigger asChild>
                      <Button variant="outline" className="text-orange-600 border-orange-300 hover:bg-orange-50">
                        <Archive className="w-4 h-4 mr-2" />
                        Archive Old Logs
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Archive Audit Logs</DialogTitle>
                        <DialogDescription>
                          Delete audit log entries older than the specified number of days. 
                          Export logs first if you need to keep a record.
                        </DialogDescription>
                      </DialogHeader>
                      <div className="py-4 space-y-4">
                        <div className="space-y-2">
                          <Label>Keep logs from the last:</Label>
                          <Select value={auditDaysToKeep} onValueChange={setAuditDaysToKeep}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="30">30 days</SelectItem>
                              <SelectItem value="60">60 days</SelectItem>
                              <SelectItem value="90">90 days</SelectItem>
                              <SelectItem value="180">180 days</SelectItem>
                              <SelectItem value="365">1 year</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <Button 
                          variant="outline" 
                          className="w-full"
                          onClick={() => archiveAuditLogs(true)}
                        >
                          <Search className="w-4 h-4 mr-2" />
                          Preview (Count logs to archive)
                        </Button>
                        {auditArchiveResults && (
                          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm">
                            <p><strong>Logs to archive:</strong> {auditArchiveResults.logs_to_archive}</p>
                            <p><strong>Cutoff date:</strong> {auditArchiveResults.cutoff_date}</p>
                          </div>
                        )}
                      </div>
                      <DialogFooter>
                        <Button variant="outline" onClick={() => setShowAuditArchiveDialog(false)}>Cancel</Button>
                        <Button 
                          variant="destructive"
                          onClick={() => archiveAuditLogs(false)}
                        >
                          Archive Logs
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Collapsible open={auditLogOpen} onOpenChange={setAuditLogOpen}>
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" className="w-full justify-between mb-3 hover:bg-gray-100">
                    <span className="font-medium">Recent Activity ({auditLog.length} entries)</span>
                    {auditLogOpen ? (
                      <ChevronDown className="w-4 h-4" />
                    ) : (
                      <ChevronRight className="w-4 h-4" />
                    )}
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  {auditLog.length === 0 ? (
                    <p className="text-center text-gray-500 py-8">No audit log entries</p>
                  ) : (
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {auditLog.map((log) => (
                        <div 
                          key={log.id} 
                          className="flex items-start gap-3 p-3 border rounded-lg hover:bg-gray-50"
                          data-testid={`audit-log-${log.id}`}
                        >
                          <div className="mt-1">
                            {getChangeTypeIcon(log.change_type)}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-sm text-blue-600">{log.action_reference}</span>
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                log.change_type === 'created' ? 'bg-green-100 text-green-700' :
                                log.change_type === 'updated' ? 'bg-blue-100 text-blue-700' :
                                log.change_type === 'archived' ? 'bg-orange-100 text-orange-700' :
                                log.change_type === 'restored' ? 'bg-purple-100 text-purple-700' :
                                log.change_type === 'deleted' ? 'bg-red-100 text-red-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                {log.change_type}
                              </span>
                            </div>
                            <p className="text-sm text-gray-700 mt-1">
                              <strong>{log.user_name}</strong>
                              {log.change_type === 'updated' && log.changes && (
                                <span className="text-gray-500">
                                  {' changed '}
                                  {Object.keys(log.changes).join(', ')}
                                </span>
                              )}
                              {log.change_type === 'created' && ' created this action'}
                              {log.change_type === 'archived' && ' archived this action'}
                              {log.change_type === 'restored' && ' restored this action'}
                              {log.change_type === 'deleted' && ' permanently deleted this action'}
                            </p>
                            <p className="text-xs text-gray-400 mt-1">
                              <Clock className="w-3 h-3 inline mr-1" />
                              {formatDate(log.timestamp)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CollapsibleContent>
              </Collapsible>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
